<template>
    <div class="prescription-component" :style="overflowStyle">
        <component :is="card ? 'card' : 'div'">
            <!-- RX SEARCH FILTER-->
            <div v-if="showSearchBar" class="mb-2">
                <SearchComponent v-model="prescription" :search-u-r-l="`/Prescription`" @input="fetchPrescription" />
                <b-button variant="info" @click="newPrescription" :disabled="patient?.deceased">New Rx</b-button>
                <hr />
            </div>

            <EntityAlertsComponent ref="entity-alerts" :patient="patient" :prescription="prescription"
                :prescriber="doctor" :drug="drug" :drug-source="drug?.source" :store-id="prescription?.storeID"
                :fill-date="prescription?.fillDate" :rx-status="statusCode" :expiration-date="expiration"
                :additional-alerts="additionalAlerts" sticky-on-scroll class="pb-3">
                <template v-if="!isBatchMode" #default>
                    <h1 class="m-0">Prescription Details</h1>
                </template>
                <template v-if="prescription.rxNumber" #accessory>
                    <div class="d-flex align-items-center mr-4" style="gap: 8px;">
                        <b-button size="sm"
                            :variant="prescription.isUrgent ? 'warning' : 'outline-warning'"
                            @click="toggleUrgent">
                            <i class="fas fa-exclamation-triangle"></i>
                            {{ prescription.isUrgent ? 'Urgent' : 'Mark Urgent' }}
                        </b-button>
                        <b-link v-b-modal.prescription-contacts-modal>Contacts <i class="fas fa-phone" /></b-link>
                        <history-modal v-model="prescription" />
                        <PatientEducationModalComponent v-if="prescription.packageID"
                            :drug-id="prescription.packageID" :drug-source="prescription.drugSource" />
                    </div>
                </template>
            </EntityAlertsComponent>

            <DispensingErrorAlertComponent v-for="(err, index) in dispenseErrors" :key="err.id"
                v-model="dispenseErrors[index]" :buttonTitles="dispenseErrorAlertButtonTitles"
                @onStatusChange="fetchDispenseError" />

            <!-- Ship to Prescriber Banner -->
            <ShipToPrescriberBanner :visible="isDrugShipToPrescriber" />

            <!-- MedSync banner + modals -->
            <MedSyncSection :patient="patient" :prescription="prescription" />

            <b-row v-show="!!(prescription && prescription.rxNumber)">
                <b-col>
                    <b-alert v-if="patient && patient.deceased" :show="true" variant="danger">
                        <i class="fas fa-exclamation-triangle"></i>
                        <strong> Patient is deceased.</strong>
                        Refills cannot be created for deceased patients.
                    </b-alert>
                    <b-alert v-if="newRefillMode" v-model="notAllowNewRefill" variant="warning">
                        This prescription <b>is not refillable</b>.
                        <span v-if="prescription && !prescription.hasBeenShipped">
                            Previous fill has not been shipped yet.
                        </span>
                    </b-alert>
                    <span v-if="newRefillMode">
                        Save changes to create new refill <strong>Rx #</strong>{{ nextRefillRxId }}
                    </span>
                    <span v-else>
                        <strong>Rx #</strong>{{ prescriptionIdentifier }}
                        <i v-if="prescription.isAutoRefillEnrolled" class="fa fa-redo text-info ml-1"
                            v-b-tooltip.hover title="Auto-Refill Enrolled" style="font-size: 0.8em;" />
                    </span>

                    <div v-if="isBatchMode &&
                        prescriptionsInBatch &&
                        prescriptionsInBatch.length > 1
                    ">
                        <b>In Batch: </b>
                        <span>({{ batchIndex + 1 }} / {{ prescriptionsInBatch.length }})</span>
                    </div>
                    <router-link v-if="patient?.id" :to="{ name: 'PatientDashboard', params: { id: patient.id } }"
                        class="ml-2 pull-right">
                        <span>Go To Profile</span> <i class="fas fa-user" />
                    </router-link>
                    <router-link
                        v-if="patient?.id && $permissions.some(p => p.permissionCodeName === 'Patient Dashboard/Create Order')"
                        :to="{ name: 'Order', params: { patientId: patient.id }, query: prescription.rxNumber ? { rxId: prescription.rxID } : {} }" class="ml-2 pull-right">
                        <span>New Order</span> <i class="fas fa-plus-circle" />
                    </router-link>
                    <router-link
                        :to="{ name: 'Tasks', params: { taskId: 0 }, query: { patientId: prescription.patientID, prescriptionId: prescription.rxID } }"
                        class="pull-right ml-2">
                        New Task <i class="fas fa-check-circle" />
                    </router-link>
                    <a v-if="$permissions.some(p => p.permissionCodeName === 'Patient Dashboard/Create PAP')" href="#"
                        class="pull-right" @click.prevent="createPAPApplication">
                        Create PAP Application <i class="fas fa-file" />
                    </a>
                </b-col>
            </b-row>

            <b-row v-if="prescription">
                <b-col class="rx-actions">
                    <template v-if="!displayEscriptID">
                        <b-button v-if="!prescription.imageID && (prescription.rxNumber || hasEditScriptPermission)" @click="openAddImage"
                            variant="danger">Add Image
                        </b-button>
                        <b-button v-if="prescription.imageID && (prescription.rxNumber || hasEditScriptPermission)" @click="openAddImage"
                            variant="danger">Replace Image
                        </b-button>
                    </template>
                    <template v-if="displayEscriptID">
                        <b-button variant="warning" v-b-modal="'all-escripts'">
                            All Escript Images
                            <escript-list-modal v-model="prescription.rxID" id="all-escripts" />
                        </b-button>
                        <b-button v-if="prescription.rxNumber && canRedrawEscript" @click="redrawEscript">
                            Redraw Escript
                        </b-button>
                    </template>
                    <b-button v-if="canEditImage" variant="warning" @click="openPdfAnnotator">
                        <i class="fas fa-edit"></i> Edit Image
                    </b-button>
                    <b-button
                        v-if="allowAnnotationRevert && sourceImageId && prescription.imageID && !displayEscriptID && hasImageEditOrAnnotatePermission"
                        :disabled="revertingImage"
                        variant="outline-warning"
                        @click="revertToOriginalImage(prescription)">
                        <i class="fas fa-undo"></i> Revert to Original
                    </b-button>
                    <b-button v-if="queueItemID" variant="success" @click="showDocuStore = true">
                        Save as Patient Document
                    </b-button>
                    <b-button v-if="queueItemID" variant="danger" @click="markAsNotAScript">
                        Not A Script
                    </b-button>
                    <b-button v-if="canMoveToBucket" variant="warning" @click="openMoveToBucketModal">
                        Move to Bucket
                    </b-button>
                    <b-button v-show="showChopperButton" v-b-modal="'pdf-choppa'" variant="info">
                        Split Document
                        <pdf-chopper-modal id="pdf-choppa" :image-id="prescription.imageID"
                            :has-saved-prescription="!!prescription.rxNumber"
                            @splitComplete="handleSplitComplete" v-model="pdfChopperVisible" />
                    </b-button>
                </b-col>
                <b-col class="text-right rx-actions">
                    <!-- GENERAL RX OPTIONS -->
                    <template v-if="prescription.rxNumber">
                    <span v-show="prescription.isActive" class="text-right" style="display: inline-block">
                        <EscriptButtonComponent v-model="prescription" :escript="escript" />
                    </span>
                    <b-button v-if="allowNewRefill && !refillBatchMode" :disabled="newRefillMode || patient?.deceased" variant="info"
                        @click="newRefill">
                        New Refill
                    </b-button>

                    <!-- The TransferMyRx status probe lives inside RxTransferActions, which only
                         renders under v-if="prescription.rxNumber". This is safe: transferMyRxId is
                         only ever set alongside a saved rxNumber (external transfer validates RxNumber
                         before assigning it), so a truthy transferMyRxId always implies a truthy rxNumber. -->
                    <RxTransferActions
                        :prescription="prescription"
                        :store="store"
                        :patient="patient"
                        :disable-internal-transfer="disableInternalTransfer"
                        @internal-transferred="onInternalTransferred"
                        @transferred="onTransferCompleted"
                        @rx-needs-refresh="fetchPrescription"
                        @status-changed="transferMyRxStatus = $event"
                        @locked="onTransferLocked" />
                    </template>
                    <ContactPrescriberActions
                        :patient="patient"
                        :prescription="prescription"
                        :doctor="doctor"
                        :program="program"
                        :store="store"
                        :drug="drugAsDrug"
                        :has-contact-prescriber-access="hasContactPrescriberAccess"
                        :has-rx-clarification-access="hasRxClarificationAccess"
                        :has-alternate-rx-access="hasAlternateRxAccess"
                        :has-provider-communications-send-permission="hasProviderCommunicationsSendPermission"
                        :show-refill-auth-request="showRefillAuthRequest"
                        @saved="updateAlerts" />
                    <template v-if="prescription.rxNumber">
                    <CreateConsultationButton :patient="patient" :store="store" :prescription="prescription" />
                    <CompoundPrintActions :prescription="prescription" />
                    <span v-show="prescription &&
                        prescription.rxNumber &&
                        prescription.pV2ID &&
                        !newRefillMode
                        ">
                        <b-button variant="danger" @click="cancelPV2"> Cancel PV2 </b-button>
                    </span>
                    <span v-show="prescription && prescription.rxNumber && prescription.isVoid">
                        <b-button variant="danger" @click="unVoid"> Unvoid </b-button>
                    </span>
                    </template>
                </b-col>
            </b-row>

            <b-row>
                <!-- PRESCRIPTION IMAGE -->
                <RxImagePanel
                    ref="imagePanel"
                    :prescription="prescription"
                    :cancel-rx-escript-i-d="cancelRxEscriptID"
                    :display-escript-i-d="displayEscriptID"
                    :is-cancel-rx-active="isCancelRxActive"
                    :store="store"
                    :escript="escript"
                    :request-reference-number="escriptResponseMessage?.requestReferenceNumber"
                    :show-cross-patient-hard-copy-scripts="showCrossPatientHardCopyScripts"
                    :hide-same-patient-hard-copy-scripts="hideSamePatientHardCopyScripts"
                    :new-refill-mode="newRefillMode"
                    :positive-id-enabled="positiveIdEnabled"
                    :positive-id-methods="positiveIdMethods"
                    @image-updated="onImageUpdated"
                    @note-consumed="onImageNoteConsumed" />
                <b-modal v-model="moveToBucketModalVisible" title="Move to Bucket" ok-title="Move"
                    :ok-disabled="!selectedMoveToBucket || isMovingToBucket" @ok.prevent="confirmMoveToBucket">
                    <b-form-group label="Destination bucket:">
                        <b-form-select v-model="selectedMoveToBucket" :options="moveToBucketOptions" />
                    </b-form-group>
                </b-modal>
                <b-col>
                    <!-- PRESCRIPTION FORM LAYOUT TOGGLE -->
                    <div class="text-right mb-2">
                        <b-form-radio-group
                            v-model="prescriptionFormLayout"
                            buttons
                            button-variant="outline-primary"
                            size="sm"
                            class="layout-toggle"
                            @change="onPrescriptionFormLayoutChange">
                            <b-form-radio value="default"
                                v-b-tooltip.hover="{ delay: { show: 750, hide: 0 } }"
                                title="Single-column layout with full-size fields">
                                <i class="fas fa-list mr-1" /> Standard
                            </b-form-radio>
                            <b-form-radio value="compact"
                                v-b-tooltip.hover="{ delay: { show: 750, hide: 0 } }"
                                title="Two-column layout that fits more on screen by placing Store/Patient and Doctor/Drug side-by-side">
                                <i class="fas fa-columns mr-1" /> Compact
                            </b-form-radio>
                        </b-form-radio-group>
                    </div>

                    <!-- PRESCRIPTION FORM -->
                    <PrescriptionFormFields
                        v-if="!showPatientForm && !editPatientForm && !doctorFormMode && !supervisorFormMode && !showDocuStore && !showPharmacyForm"
                        ref="prescriptionForm"
                        :layout="prescriptionFormLayout"
                        :form-state="prescriptionFormState"
                        :store.sync="store"
                        :patient.sync="patient"
                        :doctor.sync="doctor"
                        :supervising-physician.sync="supervisingPhysician"
                        :enable-supervising-physician="enableSupervisingPhysician"
                        :program.sync="program"
                        :drug.sync="drug"
                        :origin-code.sync="originCode"
                        :status-code.sync="statusCode"
                        :delivery-code.sync="deliveryCode"
                        :daw-code.sync="dawCode"
                        :substatus-code.sync="substatusCode"
                        :treatment-type-code.sync="treatmentTypeCode"
                        :dispensed-quantity.sync="dispensedQuantity"
                        :expiration.sync="expiration"
                        :written-date.sync="writtenDate"
                        :next-touch-date-local.sync="nextTouchDateLocal"
                        :next-touch-date-manual-override.sync="nextTouchDateManualOverride"
                        :has-active-claims="hasActiveClaims"
                        @preset-changed="onCompoundPresetChanged"
                    />

                    <RxDiagnosisComponent
                        v-if="enableRxLevelDiagnosis && !showPatientForm && !editPatientForm && !doctorFormMode && !supervisorFormMode && !showDocuStore && !showPharmacyForm"
                        v-model="prescription.diagnosisCodes"
                        :disable="disableFields"
                        :patient-id="patient && patient.id"
                        :store-id="prescription.storeID"
                        :rx-number="prescription.rxNumber"
                        :rf-number="prescription.rfNumber"
                    />

                    <PatientFormComponent v-if="editPatientForm" :button-titles="patientButtonTitles"
                        :escript-response-patient="patient" :escript-diagnoses="escriptDiagnoses"
                        :escript-allergies="escriptAllergiesList"
                        :escript-asserts-no-known-allergies="escriptAssertsNoKnownAllergies"
                        create-new-patient hide-insurance
                        @close="editPatientForm = false" @created="onPatientCreated" />

                    <PatientFormComponent v-if="showPatientForm" :button-titles="patientButtonTitles"
                        :create-new-patient="true" :escript-response-patient="escriptPatient"
                        :escript-diagnoses="escriptDiagnoses" :escript-allergies="escriptAllergiesList"
                        :escript-asserts-no-known-allergies="escriptAssertsNoKnownAllergies"
                        hide-insurance
                        @close="showPrescriptionForm('hidePatientForm')" @created="onPatientCreated" />

                    <PrescriberFormPanel v-if="doctorFormMode"
                        :mode="doctorFormMode"
                        :prescriber="doctor"
                        :escript-prescriber="escriptPrescriber"
                        :button-titles="doctorButtonTitles"
                        @close="onDoctorFormClose"
                        @saved="onDoctorSaved" />

                    <PrescriberFormPanel v-if="supervisorFormMode"
                        :mode="supervisorFormMode"
                        :prescriber="supervisingPhysician"
                        :escript-prescriber="escriptSupervisor"
                        :button-titles="doctorButtonTitles"
                        @close="onSupervisorFormClose"
                        @saved="onSupervisorSaved" />

                    <pharmacy-detail-page v-if="showPharmacyForm"
                        :button-titles="{ save: 'Save Changes', cancel: 'Close', new: '', clear: '' }" new-mode
                        @close="onPharmacyFormClose" @created="onPharmacyCreate" />

                    <PatientDocumentsForm v-if="showDocuStore" ref="patientDocumentsForm" :queueItem="queueItem"
                        :allow-process-as-prescription="true" task-on-save
                        @close="showPrescriptionForm()" @on-save="handlePatientDocSaved"
                        @process-as-prescription="showPrescriptionForm()" />
                </b-col>
            </b-row>

            <hr />
            <b-alert v-model="showDrugAlert" dismissible variant="danger">
                <b>Drug Memo:</b> {{ drugAlert }}
            </b-alert>
            <b-alert v-model="showAlertIsOutOfState" variant="warning">
                <b>Prescription cannot be filled at this store.</b> Please transfer to appropriate
                store licensed in patient's state.
            </b-alert>
            <b-alert v-model="showInactiveDrugAlert" :variant="isHubMode ? 'warning' : 'danger'">
                <template v-if="isHubMode">
                    <b>Selected drug is not active.</b> In Hub Mode, prescriptions with inactive drugs can only be saved as On Hold, Void, or Transfer Out.
                </template>
                <template v-else>
                    <b>Selected drug is not active</b>. Please select an active drug.
                </template>
            </b-alert>
            <b-alert v-model="showAgeAlert" variant="warning">
                <b>Prescription Fill Date too old, Manager Intervention Required</b>
            </b-alert>
            <!-- Custom Questions-->
            <b-row v-for="cat in drugCategories" :key="cat.id">
                <b-col>
                    <QuestionaryComponent :ref="`questionary_${cat.id}`" :categoryID="+cat.id"
                        :disable="!prescription.isActive || !canAnswerPrescriptionQuestions" :prescription="prescription">
                    </QuestionaryComponent>
                </b-col>
            </b-row>

            <h4 ref="insuranceBillingSection">Insurance Billing</h4>
            <b-row>
                <b-col>
                    <InsuranceBillingComponent ref="insuranceComponent" :billOnlyMultilfills="isMultiFill"
                        :defaultPriceSchedule="program && program.priceSchedule" :disabled="disableInsuranceBilling"
                        :drugLicenseType="drugLicenseType" :newMode="newMode || newRefillMode" :patient="patient"
                        :prescription="prescription" :isMedSyncShortFill="medSyncShortFillData?.isMedSyncShortFill || false"
                        :statusCode="statusCode"
                        @clearRx="clearForm" @onClaimsLoaded="claimsLoaded"
                        @startSurescriptsElectronicPriorAuthorization="startSurescriptsElectronicPriorAuthorization
                        " @startSurescriptsElectronicPriorAuthorizationExpedited="startSurescriptsElectronicPriorAuthorizationExpedited
                        " @save-with-plan="saveAndBill" @rx-needs-refresh="fetchPrescription(prescription)"
                        @rx-transfer-refresh="rxTransferRefresh" @pricing-updated="updatePricing" />
                </b-col>
            </b-row>
            <hr />
            <!-- Hard Copy Note -->
            <template v-if="validFormForBilling && hasHardcopyPermission && canAnnotate && !isCancelRxActive">
                <b-row>
                    <b-col>
                        <fg-input label="Hardcopy Note">
                            <textarea v-model="addNote" class="textarea-autosize form-control" placeholder="Enter note text to add to the prescription image..." />
                        </fg-input>
                        <div class="d-flex align-items-center">
                            <b-button :disabled="!validFormForBilling ||
                                (prescription && !prescription.isActive) ||
                                !hasHardcopyPermission ||
                                !addNote.trim()" variant="success" @click="openQuickAnnotate">
                                Add Hardcopy Note
                            </b-button>
                            <b-form-checkbox v-model="keepHardcopyNote" switch class="ml-3 mb-0">
                                Keep note after save
                            </b-form-checkbox>
                        </div>
                    </b-col>
                </b-row>
                <hr />
            </template>
            <!-- Patient Notes -->
            <b-row v-if="patient && patient.id">
                <b-col>
                    <NotesForm v-if="patient" :entityId="+patient.id" :entityType="patientEntityType"
                        :saveButtonLabel="'Add Patient Note'" :textBoxLabel="'Patient Note'"
                        @saved="refreshPatientNotes" />
                    <NotesList ref="patientNotesList" :entityId="+patient.id" :entityType="patientEntityType"
                        :pageSize="5" />
                </b-col>
            </b-row>

            <hr v-if="prescription" />

            <b-overlay :show="saving">
                <NewClearSaveButtons :titles="buttonTitles" :disable-new="disableNew" :disable-clear="disableClear"
                    :disable-save="disableSave" :disable-cancel="saving" show-clear-modal @cancel-clicked="cancel"
                    @clear-clicked="undoChanges" @new-clicked="saveAndContinue" @save-clicked="save"
                    @save-and-stay-clicked="saveAndStay" />
            </b-overlay>
        </component>

        <DebugViewComponent header="">
            <b-row>
                <b-col>
                    disableFieldsReason: {{ disableFieldsReason }}
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    disableStatusFieldsReason: {{ disableStatusFieldsReason }}
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    disableBillingReason: {{ disableBillingReason }}
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    disableInsuranceBillingReason: {{ disableInsuranceBillingReason }}
                </b-col>
            </b-row>
        </DebugViewComponent>
        <!-- Error resolve confirmation -->
        <DispenseErrorResolveModal ref="dispenseErrorResolveModal" :dispense-error="dispenseError"
            :dispense-error-label="dispenseErrorLabel" @resolved="onDispenseErrorResolved"
            @proceed="onDispenseErrorProceed" />

        <b-modal id="prescription-contacts-modal" title="Prescription Contacts" hide-footer>
            <b-table :items="prescriptionContacts" :fields="prescriptionContactFields" no-provider-sorting />
        </b-modal>

        <PatientRxHistoryModalComponent id="prescription-details-rx-history" ref="patient_rx_history_modal"
            :patient="patient" />

        <BrandToGenericPromptModal ref="brandToGenericPromptModal"
            @use-generic="onUseGenericFromPrompt"
            @keep-brand="onKeepBrandFromPrompt"
            @dismissed="onBrandToGenericDismissed" />

        <DiscontinueEquivalentScriptsModal
            v-model="showEquivalentModal"
            :patient-id="prescription.patientID"
            :package-id="prescription.packageID"
            :current-store-id="prescription.storeID"
            :current-rx-number="prescription.rxNumber"
            :initial-equivalent-prescriptions="equivalentPrescriptionsData"
            @discontinued="onEquivalentDiscontinued"
            @skipped="onEquivalentSkipped"
            @dismissed="onEquivalentDismissed"
        />

        <patient-notes-modal-component v-if="prescription && prescription.patientID"
            :entity-id="prescription.patientID" />
        <doctor-notes-modal-component v-if="prescription && prescription.prescriberID"
            :entity-id="prescription.prescriberID" />
        <drug-notes-modal-component v-if="prescription && prescription.packageID" :entity-id="prescription.packageID"
            :drug-source="prescription.drugSource" />



        <PositiveIdGate
            ref="positiveIdGate"
            :enabled="positiveIdEnabled"
            :enabled-methods="positiveIdMethods"
            :action-type="PositiveIdActionType.RxEntry"
            :target-entity-type="PositiveIdTargetEntityType.Patient"
            :target-entity-id="positiveIdTargetId || 0"
            @verified="onPositiveIdVerifiedForSave"
            @cancelled="onPositiveIdCancelledForSave" />
    </div>
</template>

<script lang="ts">
import { Component, Mixins, Prop, Ref, Watch, Vue } from "vue-property-decorator";
import Axios, { AxiosError } from "axios";
import { getDrugImageUrls } from "@/util/DrugImage";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import NewClearSaveButtons from "@/components/NewClearSaveButtons.vue";
import PositiveIdGate from "@/components/Common/PositiveIdGate.vue";
import { PageFocusEventType, PageFocusEventClosedAction } from "@/models/PageFocusEvent";
import PageFocusEventService from "@/services/PageFocusEventService";
import PositiveIdService, {
    PositiveIdActionType,
    PositiveIdTargetEntityType,
} from "@/services/PositiveIdService";
import EnumSelector from "@/components/EnumSelector.vue";
import DispensingErrorAlertComponent from "@/components/DispensingError/DispensingErrorAlertComponent.vue";
import NotesForm from "@/components/Note/NotesForm.vue";
import NotesList from "@/components/Note/NotesList.vue";
import InsuranceBillingComponent from "@/components/InsurancePlan/InsuranceBillingComponent.vue";
import RxImagePanel from "@/components/Prescription/RxImagePanel.vue";
import EscriptListModal from "@/components/Prescription/EscriptListModal.vue";
import PdfChopperModal from "@/components/Prescription/PdfChopperModal.vue";
import EquivalentDrugsComponent from "@/components/Drug/EquivalentDrugsComponent.vue";
import QuestionaryComponent from "@/components/Questionary/QuestionaryComponent.vue";
import { Questionary } from "@/models/Questionary";
import { Lock, LockHandler } from "@/mixins/LockHandler";
import AutoModeHotkeyMixin from '@/mixins/AutoModeHotkeyMixin';
import PhoneFormatMixin from "@/mixins/PhoneFormatMixin";
import AnnotationRevertMixin from "@/mixins/AnnotationRevertMixin";
import {
    DAWCode,
    OriginCode,
    Prescription,
    PrescriptionDispenseItem,
    RefillStatusCode,
    RxDeniedSubStatusId,
    TreatmentType,
} from "@/models/Prescription";
import {
    validateForBilling,
    BillingValidationInput,
    BillingValidationResult,
} from "@/services/prescription/billingValidation";
import { hydrateViewValues, applyViewValues } from "@/services/prescription/rxViewValues";
import { clampFillDateToEffective as clampFillDateToEffectivePure } from "@/services/prescription/dateRules";
import { SaveIntent, createSaveIntent } from "@/services/prescription/saveIntent";
import {
    PrescriptionSaveOrchestrator,
    SaveOrchestratorState,
    SavedPrescription,
} from "@/services/prescription/PrescriptionSaveOrchestrator";
import { buildSavePorts } from "@/services/prescription/buildSavePorts";
import {
    decidePostSaveContinuation,
    decideBackDestination,
    captureFillIdentity,
    BilledFill,
    PostSaveAction,
    PostSaveContext,
    BackDestination,
} from "@/services/prescription/saveDecisions";
import { EscriptService } from "@/services/EscriptService";
import { PrescriptionService } from "@/services/PrescriptionService";
import { IngestionQueueService } from "@/services/IngestionQueueService";
import { DrugService } from "@/services/DrugService";
import CompoundPrintActions from "@/components/Prescription/CompoundPrintActions.vue";
import { SigTranslationService } from "@/services/SigTranslationService";
import { PriorAuthorizationService } from "@/services/PriorAuthorizationService";
import { ImageService } from "@/services/ImageService";
import { DeliveryCode } from "@/models/DeliveryCode";
import { Prescriber } from "@/models/Prescriber";
import { Patient } from "@/models/Patient";
import { Program } from "@/models/Program";
import { DispenseError } from "@/models/DispenseError";
import { Store, StoreCapabilities } from "@/models/Store";
import { QueueItem } from "@/models/QueueItem";
import { MoveToBucketDestinationFilter } from "@/models/Configuration";
import { buildMoveToBucketOptions, BucketOption, BucketDestination, NamedRecord } from "@/util/moveToBucketOptions";
import { Drug, DrugStatus } from "@/models/Drug/Drug";
import { DrugCategory } from "@/models/DrugCategory";
import { Escript, EscriptResponse, EscriptResponseMessage } from "@/models/Escript";
import { Location } from "vue-router";
import { DatePicker } from "element-ui";
import { Note } from "@/models/Note";
import PatientFormComponent from "@/components/Patient/PatientFormComponent.vue";
import PatientRxHistoryModalComponent from "@/components/Patient/PatientRxHistoryModalComponent.vue";
import ContactPrescriberActions from "@/components/Prescription/ContactPrescriberActions.vue";
import BrandToGenericPromptModal, { KeepBrandPayload } from "@/components/Prescription/BrandToGenericPromptModal.vue";
import DiscontinueEquivalentScriptsModal from "@/components/Prescription/DiscontinueEquivalentScriptsModal.vue";
import type { EquivalentPrescription } from "@/models/EquivalentPrescription";
import PrescriberFormPanel, { PrescriberFormMode } from "@/components/Doctor/PrescriberFormPanel.vue";
import DispenseErrorResolveModal from "@/components/Prescription/DispenseErrorResolveModal.vue";
import PatientDocumentsForm from "@/components/Patient/PatientDocumentsForm.vue";
import DoctorFollowUpCasesAlertComponent from "@/components/Doctor/DoctorFollowUpCasesAlertComponent.vue";
import moment from "moment";
import { Claim } from "@/models/Claim";
import DirectionsComponent from "@/components/Prescription/DirectionsComponent.vue";
import EscriptButtonComponent from "@/components/Prescription/EscriptButtonComponent.vue";
import SearchComponent from "@/components/SearchComponent.vue";
import { ValidatorGroup } from "@/util/ValidatorGroup";
import { AutoRefillSubscription } from "@/models/AutoRefillSubscription";
import { DrugProduct, DrugSource } from "@/models/Drug/DrugProduct";
import { NonGSDDDrug } from "@/models/Drug/NonGSDDDrug";
import { GSDDPatientDiagnosis } from "@/models/GSDD";
import { MacOrigin } from "@/models/MacOrigin";
import { CompoundDrug } from "@/models/Drug/CompoundDrug";
import CompoundIngredientsDisplay from "@/components/Compound/CompoundIngredientsDisplay.vue";
import { PharmacyRecord } from "@/models/Pharmacy";
import PharmacyDetailPage from "@/pages/Pharmacy/PharmacyDetailPage.vue";
import PharmacistDetailPage from "@/pages/Pharmacist/PharmacistDetailPage.vue";
import { PharmacyBenefitManager } from "@/models/PharmacyBenefitManager";
import { BvTableCtxObject } from "bootstrap-vue";
import DebugViewComponent from "@/components/DebugViewComponent.vue";
import { OrderStatus } from "@/models/Order";
import { EntityType } from "@/models/EntityType";
import HistoryModal from "@/components/Modals/HistoryModal.vue";
import HelpPopup from "@/components/HelpPopup.vue";
import DoctorNotesModalComponent from "@/components/Note/DoctorNotesModalComponent.vue";
import PatientNotesModalComponent from "@/components/Note/PatientNotesModalComponent.vue";
import DrugNotesModalComponent from "@/components/Note/DrugNotesModalComponent.vue";
import DrugInfoComponent from "@/components/Drug/DrugInfoComponent.vue";
import PatientEducationModalComponent from "@/components/Drug/PatientEducationModalComponent.vue";
import { PatientDocument } from "@/models/PatientDocument";
import EntityAlertsComponent, { Alert } from "@/components/EntityAlertsComponent.vue";
import RxTransferListModalComponent from "@/components/Patient/RxTransferListModalComponent.vue";
import { providerCommunicationsPermissions } from "@/services/providerCommunicationsService";
import SearchPrescriptionDetailComponent from "@/components/Prescription/SearchPrescriptionDetailComponent.vue";
import PrescriptionFormFields from "@/components/Prescription/PrescriptionFormFields.vue";
import RxDiagnosisComponent from "@/components/Prescription/RxDiagnosisComponent.vue";
import { PrescriptionFormState } from "@/models/PrescriptionFormState";
import RxTransferActions from "@/components/Prescription/RxTransferActions.vue";
import CreateConsultationButton from "@/components/Consultation/CreateConsultationButton.vue";
import MedSyncSection from "@/components/Prescription/MedSyncSection.vue";

let processingInsurance = false;
let previousRoute: any;

// One definition of the per-prescription lock URL: registering the saved Rx's lock compares against
// the lock the page already holds, so the two paths must build it identically.
const prescriptionLockUrl = (rxID: string) => `/Prescription/${rxID}/Lock`;

// The boolean feature flags loaded for the prescription page (POST /api/Configuration/values).
// Typed so the loader maps each flag by name instead of an untyped Record<string, boolean>.
interface PrescriptionConfigFlags {
    HideDispensingFields: boolean;
    disableFutureWrittenDates: boolean;
    EnableEquivalentDrugCheck: boolean;
    EnforcePackageSizeBilling: boolean;
    AllowAnnotationRevert: boolean;
    SuppressEscriptDiagnosisAlert: boolean;
    EnforceEscriptEffectiveDate: boolean;
    RequireDirectionsAtEntry: boolean;
    ShowCrossPatientHardCopyScripts: boolean;
    HideSamePatientHardCopyScripts: boolean;
    PromptGenericSubstitutionOnBrand: boolean;
    EnableSupervisingPhysician: boolean;
    EnableRxLevelDiagnosis: boolean;
    ShowMoveToBucketOnProcessing: boolean;
    MoveToBucketDestinationFilter: MoveToBucketDestinationFilter;
}

const PRESCRIPTION_CONFIG_KEYS: (keyof PrescriptionConfigFlags)[] = [
    "HideDispensingFields", "disableFutureWrittenDates", "EnableEquivalentDrugCheck",
    "EnforcePackageSizeBilling", "AllowAnnotationRevert", "SuppressEscriptDiagnosisAlert",
    "EnforceEscriptEffectiveDate", "RequireDirectionsAtEntry", "ShowCrossPatientHardCopyScripts",
    "HideSamePatientHardCopyScripts", "PromptGenericSubstitutionOnBrand", "EnableSupervisingPhysician",
    "EnableRxLevelDiagnosis", "ShowMoveToBucketOnProcessing", "MoveToBucketDestinationFilter",
];

@Component({
    computed: {
        PharmacyBenefitManager() {
            return PharmacyBenefitManager;
        },
    },
    beforeRouteLeave(_to, _from, next) {
        if (processingInsurance) {
            alert("Please wait until insurance processing has finished");
            next(false);
        } else {
            next();
        }
    },
    beforeRouteEnter(_to: any, from: any, next: any) {
        previousRoute = from;
        next();
    },
    name: "PrescriptionDetailsPage",
    components: {
        SearchPrescriptionDetailComponent,
        PrescriptionFormFields,
        RxDiagnosisComponent,
        DrugNotesModalComponent,
        PatientNotesModalComponent,
        DoctorNotesModalComponent,
        HistoryModal,
        DebugViewComponent,
        PharmacistDetailPage,
        DirectionsComponent,
        NewClearSaveButtons,
        PositiveIdGate,
        EnumSelector,
        InsuranceBillingComponent,
        RxImagePanel,
        EscriptListModal,
        PdfChopperModal,
        [DatePicker.name]: DatePicker,
        PatientFormComponent,
        PrescriberFormPanel,
        DispensingErrorAlertComponent,
        NotesForm,
        NotesList,
        DispenseErrorResolveModal,
        EquivalentDrugsComponent,
        EscriptButtonComponent,
        QuestionaryComponent,
        PatientRxHistoryModalComponent,
        PatientDocumentsForm,
        ContactPrescriberActions,
        BrandToGenericPromptModal,
        DiscontinueEquivalentScriptsModal,
        DoctorFollowUpCasesAlertComponent,
        SearchComponent,
        PharmacyDetailPage,
        HelpPopup,
        DrugInfoComponent,
        PatientEducationModalComponent,
        EntityAlertsComponent,
        RxTransferActions,
        CreateConsultationButton,
        CompoundIngredientsDisplay,
        CompoundPrintActions,
        MedSyncSection,
        ShipToPrescriberBanner: () => import("@/components/Order/ShipToPrescriberBanner.vue"),
    },
})
export default class PrescriptionDetailsPage extends Mixins(LockHandler, AutoModeHotkeyMixin, PhoneFormatMixin, AnnotationRevertMixin) {
    @Prop() prescriptionsInBatch!: Array<Prescription>;
    @Prop({ default: true, type: Boolean }) card!: Boolean;
    @Ref('positiveIdGate') positiveIdGate!: any;
    @Ref('prescriptionForm') prescriptionForm!: PrescriptionFormFields;

    PositiveIdActionType = PositiveIdActionType;
    PositiveIdTargetEntityType = PositiveIdTargetEntityType;
    positiveIdEnabled = false;
    positiveIdMethods: string[] | null = null;
    positiveIdConfigLoaded = false;
    verifiedToken = '';

    get positiveIdTargetId(): number | null {
        const n = +(this.patient?.id as any);
        return Number.isFinite(n) && n > 0 ? n : null;
    }
    @Ref("doctor-followup-alerts") doctorFollowupAlerts!: DoctorFollowUpCasesAlertComponent;
    @Ref('rxTransfersModalComponent') rxTransfersModalComponent!: RxTransferListModalComponent;
    @Ref('insuranceComponent') insuranceComponent!: InsuranceBillingComponent;
    @Ref("entity-alerts") entityAlertsComponent!: EntityAlertsComponent;
    @Ref('imagePanel') imagePanel!: RxImagePanel;
    @Ref('brandToGenericPromptModal') brandToGenericPromptModal!: BrandToGenericPromptModal;
    @Ref('dispenseErrorResolveModal') dispenseErrorResolveModal!: DispenseErrorResolveModal;

    // Proxy getter for child component ref (used by searchProgramParamsChanged watcher)
    get programSearchComponent(): SearchComponent | undefined {
        return this.prescriptionForm?.programSearchComponent;
    }

    errorLoadingDrugDetails = false;

    directionsTranslations: { paraphrases: { id: string, token: string }[], token: string } = {} as any;
    selectedTranslation: string = "";
    loadingDirections: boolean = false;

    // Enumerations
    protected originCodes = OriginCode;
    protected dawCodes = DAWCode;
    protected deliveryCodes = DeliveryCode;
    protected treatmentTypeCodes = TreatmentType;
    protected doctorButtonTitles = {
        new: "",
        clear: "",
        save: "Save Doctor",
        cancel: "Close Doctor Form",
    };
    protected prescriptionFormLayout: "default" | "compact" = "default";

    get prescriptionFormLayoutSetting(): string | undefined {
        return (this.$root as any).$data.userSettings?.['prescription-form-layout'];
    }

    @Watch("$root.$data.userSettings", { immediate: true })
    onUserSettingsLoaded() {
        this.applyPrescriptionFormLayout();
    }

    @Watch("prescriptionFormLayoutSetting")
    onPrescriptionFormLayoutSettingChanged() {
        this.applyPrescriptionFormLayout();
    }

    private applyPrescriptionFormLayout() {
        const saved = this.prescriptionFormLayoutSetting;
        if (saved === "compact" || saved === "default") {
            this.prescriptionFormLayout = saved;
        }
    }

    onPrescriptionFormLayoutChange(value: "default" | "compact") {
        (this as any).$saveUserSetting?.("prescription-form-layout", value);
    }

    protected imageURLs: string[] = [];
    protected imageError: boolean = false;
    protected equivalentDrugs: Drug.EquivalentDrug[] = [];

    protected patientButtonTitles = {
        new: "",
        clear: "",
        save: "Save Patient",
        cancel: "Close Patient Form",
    };

    //Props
    @Prop({ default: false }) private refillBatchMode!: boolean;
    @Prop({ default: false, type: Boolean }) private suppressBatchNotifications!: boolean;
    @Prop() private medSyncShortFillData?: {
        adjustedDaysSupply: number;
        adjustedDispensed: number;
        isMedSyncShortFill: boolean;
        medSyncAnchorDate: Date;
    };
    // Entities
    private prescription: Prescription = new Prescription();
    private store: Store = new Store();
    private patient: Patient = new Patient();
    private doctor: Prescriber = new Prescriber();
    private supervisingPhysician: Prescriber | null = null;
    private patientPrescribers: Prescriber[] = [];
    private program: Program = new Program();
    private doctorProgram: Program = new Program();
    private drug: DrugProduct = new Drug();
    private escript: Escript = new Escript();
    private claims: Array<Claim> = [];
    private drugCategories: Array<DrugCategory> = [];
    private prescriptionLock: Lock | null = null;
    protected queueItem: QueueItem | null = null;
    private savedIngestionQueueItemID: string | null = null;
    private savedEscriptID: string | undefined = undefined;
    private cancelRxEscriptID: string | null = null;
    private savedProgramTransfer: string | undefined = undefined;
    private lastFill: Prescription = new Prescription();
    private latestFillStatus: { status: RefillStatusCode; subStatus: number | null; rfNumber: number } | null = null;
    private autoRefillSubscription: AutoRefillSubscription = new AutoRefillSubscription();
    private drugAlert: string = "";
    protected showDrugChange = false;
    private dispenseError: DispenseError = new DispenseError();
    private dispenseErrors: Array<DispenseError> = [];
    protected patientEntityType = EntityType.Patient;
    private escriptResponseMessage: EscriptResponseMessage | null = null;
    private originalFillDate: Date = new Date();
    protected originPharmacy: PharmacyRecord = new PharmacyRecord();
    private fetchEscriptAbortController: AbortController | null = null;
    private pendingScrollToBilling = false;

    // Background-only focused-time tracking, mirroring PrescriptionVerificationDetailsPage's timer.
    private rxFocusVisibleSeconds: number = 0;
    private rxFocusVisibleInterval: number | null = null;
    private rxFocusLoadedAt: number = 0;
    private rxFocusLastRecordedRxId: string | null = null;
    private rxFocusReopenedAfterSave: boolean = false;

    // Arguments - now passed as route props
    @Prop() imageID?: string;
    @Prop() escriptID?: string;
    @Prop() queueItemID?: string;
    @Prop() programTransfer?: string;

    //Flags
    private newMode = false;
    private fromIngestion = false;
    private newRefillMode = false;
    private isScheduleClassDrug = false;
    //View values
    private dispensedQuantity: number = 0;
    private originCode: number = 0;
    private statusCode: number = 0;
    private deliveryCode: number = 0;
    private dawCode: number = 0;
    private substatusCode: number | null = null;
    private treatmentTypeCode: number | null = null;
    private nextTouchDateLocal: string = "";
    private nextTouchDateManualOverride: boolean = false;
    private settingViewValues: boolean = false;
    private requireNextTouchDateOnHold: boolean = false;
    private statusCodes = RefillStatusCode;
    protected drugStatuses: string[] = [
        "Inactive",
        "Active",
        "Active and Preferred",
        "Blocked From Fills",
    ];
    // Buttons config
    private buttonTitles = {
        new: "",
        clear: "Clear",
        save: "Save Changes",
        cancel: "",
    };

    // disableFieldsReason / disableBillingReason are pure computeds (see below);
    // they render only in DebugViewComponent.

    //Notes
    private addNote = "";
    private keepHardcopyNote = false;
    private showCrossPatientHardCopyScripts = false;
    private hideSamePatientHardCopyScripts = false;
    protected editPatientForm = false;
    protected showPatientForm = false;
    protected showDocuStore = false;
    protected showEquivalentModal = false;
    protected equivalentPrescriptionsData: EquivalentPrescription[] = [];
    protected enableEquivalentDrugCheck = false; // configuration setting - when true, runs the equivalent drug check on save
    protected enableSupervisingPhysician = false; // configuration setting - gates the per-prescription supervising-physician UI/validation
    protected enableRxLevelDiagnosis = false; // configuration setting - gates the Rx-level diagnosis code selection UI
    protected enforcePackageSizeBilling = false;
    protected suppressEscriptDiagnosisAlert = false;
    protected showPharmacyForm = false;
    protected doctorFormMode: PrescriberFormMode | null = null;
    protected supervisorFormMode: PrescriberFormMode | null = null;
    private drugDetails: any = null;
    protected drugInventory: any = null;
    protected nextRefillRxId = "";
    //--------------
    protected isClaimsLoading = true;
    private initialDispensedQty = 0;
    private expiration: Date | null = null;
    private writtenDate: Date | any = null;
    private enableExpirationDateEstimation = true;
    protected loadingDrugDetails = false;
    private batchIndex: number = -1;
    // validation
    private validationClasses: {
        quantity: string;
        dispense: string;
        refills: string;
        daySupply: string;
        fillDate: string;
        writtenDate: string;
        expirationDate: string;
    } = {
        quantity: "",
        dispense: "",
        refills: "",
        daySupply: "",
        fillDate: "",
        writtenDate: "",
        expirationDate: "",
    };
    // Save-flow reactive state, owned by the page and driven by the orchestrator.
    // Template reactivity flows solely through this observable object.
    private saveState: SaveOrchestratorState = Vue.observable({ saving: false, awaiting: null, checkingEquivalentDrugs: false });
    // Assigned in created() (NOT an initialized field) so vue-class-component does not
    // deep-observe the orchestrator, its ports, and its private latches as reactive data.
    orchestrator!: PrescriptionSaveOrchestrator;

    get saving(): boolean { return this.saveState.saving; }
    set saving(v: boolean) { this.saveState.saving = v; }
    get checkingEquivalentDrugs(): boolean { return this.saveState.checkingEquivalentDrugs; }
    set checkingEquivalentDrugs(v: boolean) { this.saveState.checkingEquivalentDrugs = v; }

    private billedNewRxAwaitingClaims: boolean = false;
    // The rxID the awaiting-claims arm was set for. Scopes the arm to a specific Rx so that a
    // claims response discarded after an Rx swap (the billing child drops it and never emits)
    // can't leave the boolean armed and let a later, unrelated claims load stamp supersede onto
    // the wrong prescription.
    private billedNewRxAwaitingClaimsRxId: string | null = null;
    // The billed fill the Save-and-New supersede latch is armed on (null = unarmed), captured
    // when its claims return. Carries the fill identity (captureFillIdentity) so the save flow
    // supersedes only when the drug or written date changed from the billed fill.
    private supersedeBilledFill: BilledFill | null = null;

    protected pdfChopperVisible: boolean = false;
    private prescriptionImagePages: number = 0;

    private showDispensingFields = true;
    private disableFutureWrittenDates = false;
    private enforceEscriptEffectiveDate = false;
    private requireDirectionsAtEntry = false;
    private promptGenericSubstitutionOnBrand = false;

    // Move-to-bucket action (Rx Intake Queue processing screen)
    private showMoveToBucketOnProcessing = false;
    private moveToBucketDestinationFilter: MoveToBucketDestinationFilter = "All";
    private moveToBucketPrograms: NamedRecord[] = [];
    private moveToBucketStaticBuckets: NamedRecord[] = [];
    private moveToBucketModalVisible = false;
    private selectedMoveToBucket: BucketDestination | null = null;
    private isMovingToBucket = false;
    // Stash for the dispense-error resolve-confirmation modal (Stage A, page-managed): the
    // save intent to resume once the operator confirms. The orchestrator's own awaiting-states
    // cover the positive-ID / generic / equivalents gates.
    private dispenseResolveIntent: SaveIntent | null = null;
    // Set by an entry point immediately before requestSave: true only for save()/saveAndStay()
    // (the equivalents-gate entry points). The orchestrator consumes it via the snapshot.
    private runEquivalentsGateOnce = false;
    // The drug-category answers submitted with the in-flight save; applySaved re-baselines them.
    private lastSubmittedDrugCategoryAnswers: unknown[] = [];
    private transferMyRxStatus = "";
    protected programSetFromTransfer: boolean = false;
    // Monotonic counter shared by fetchDrug and getDrugDetails. A superseded
    // GET /Drug/{id} must not clear the loading flag or raise an error banner
    // belonging to a newer, still in-flight request.
    private drugFetchSeq = 0;

    get showChopperButton(): boolean {
        return this.prescriptionImagePages > 1;
    }

    get overflowStyle() {
        if (this.pdfChopperVisible) return "overflow:hidden;";
        return "overflow:inherit;";
    }

    get drugNdc(): string {
        return this.drugDetails?.ndc ?? "";
    }

    get drugPackageSize(): string {
        return (this.drugDetails && `${this.drugDetails.packageSize} ${this.drugDetails.billingUnit}`) || "";
    }

    protected drugLastFilled: string = "";

    @Watch('prescriptionsInBatch')
    @Watch('patient.id')
    @Watch('prescription.rxID')
    async fetchDrugLastFilled(): Promise<string> {
        let rxId = this.prescription.rxID;
        const drugSource = this.prescription.drugSource;
        const drugId = this.prescription.packageID;
        this.drugLastFilled = "";
        if (this.patient.id == undefined || drugSource == undefined || !this.prescription.hasFullId) {
            return this.drugLastFilled;
        }
        try {
            const lastFilled = await DrugService.getPatientDrugHistory(this.patient.id, drugSource, drugId, rxId);
            // Only set drugLastFilled if the response contains a valid date
            if (lastFilled) {
                const momentDate = moment(lastFilled);
                if (momentDate.isValid()) {
                    this.drugLastFilled = momentDate.format("L");
                }
            }
        } catch (err) {
            //swallow
        }
        return this.drugLastFilled;
    }

    get drugStatus(): string {
        if (!isNaN(this.drug.activeStatus)) return this.drugStatuses[this.drug.activeStatus];

        if (!this.drugDetails) return "";
        if (!isNaN(this.drugDetails?.activeStatus)) return "";
        return this.drugStatuses[this.drugDetails.activeStatus];
    }

    get drugInStock(): boolean {
        return this.drugInventory && this.drugInventory.quantity - this.drugInventory.allocated > 0;
    }

    get drugOutOfStock(): boolean {
        return this.drugInventory && this.drugInventory.quantity - this.drugInventory.allocated <= 0;
    }

    get isDrugShipToPrescriber(): boolean {
        return this.drugCategories?.some(
            (c) => c.name?.toLowerCase() === "ship to prescriber"
        ) ?? false;
    }

    get drugAsDrug(): Drug {
        return this.drug as Drug;
    }

    get drugSearchParams(): { getProgram: boolean; drugSource?: number } {
        const params: { getProgram: boolean; drugSource?: number } = { getProgram: true };
        // Check drug.source first (available immediately on selection),
        // then fall back to prescription.drugSource (for existing prescriptions)
        const drugSource = this.drug?.source ?? this.prescription?.drugSource;
        if (drugSource !== undefined && drugSource !== null) {
            params.drugSource = drugSource;
        }
        return params;
    }

    get dispenseErrorAlertButtonTitles(): any {
        return {
            resolve: "Resolve",
            awaitingResponse: "Awaiting Response",
        };
    }

    get showDrugAlert() {
        return this.drugAlert.length > 0;
    }

    get escriptPatient() {
        return this.escriptResponseMessage?.patient ?? null;
    }

    get escriptDiagnoses(): GSDDPatientDiagnosis[] {
        const drugs = this.escriptResponseMessage?.drugs;
        if (!drugs?.diagnosisPrimaryCode) return [];

        const makeDiagnosis = (code: string, description: string): GSDDPatientDiagnosis => ({
            icD10Code: code,
            icD10Description: description || '',
            primaryPrograms: [],
            created: new Date(),
            patientId: 0,
            id: 0,
            createdBy: '',
            removed: null,
            removedBy: '',
            isUtc: false,
            origin: MacOrigin.Provider,
        });

        const diagnoses = [makeDiagnosis(drugs.diagnosisPrimaryCode, drugs.diagnosisPrimaryDescription)];

        if (drugs.diagnosisSecondaryCode) {
            diagnoses.push(makeDiagnosis(drugs.diagnosisSecondaryCode, drugs.diagnosisSecondaryDescription));
        }

        return diagnoses;
    }

    get escriptAllergiesList() {
        return this.escriptResponseMessage?.allergies ?? [];
    }

    get escriptAssertsNoKnownAllergies() {
        return this.escriptResponseMessage?.assertsNoKnownAllergies ?? false;
    }

    get escriptPrescriber() {
        return this.escriptResponseMessage?.prescriber ?? null;
    }

    get escriptSupervisor() {
        return this.escriptResponseMessage?.supervisor ?? null;
    }

    get hasSuperAdminPermission() {
        return this.$permissions.some(p => p.permissionCodeName.toLowerCase() === "Super Admin/Prescription".toLowerCase());
    }

    // Mirrors the server-side gate on drug-category answer writes: only these users' answers persist,
    // so the questionary inputs are disabled for everyone else (avoids typing answers that get dropped).
    get canAnswerPrescriptionQuestions() {
        return this.$permissions.some(p =>
            p.permissionCodeName === "Pharmacists/Answer Prescription Questions" ||
            p.permissionCodeName === "Super Admin/Prescription");
    }


    get disableSave(): boolean {
        if (this.hasSuperAdminPermission) return this.saving || this.checkingEquivalentDrugs;
        if (this.saving || this.checkingEquivalentDrugs) return true;
        if (this.patient?.deceased && (this.newRefillMode || this.newMode)) return true;
        return (!this.validForm || !(this.prescription && this.prescription.isActive));
    }

    get disableNew(): boolean {
        return this.disableSave;
    }

    get disableClear(): boolean {
        if (this.saving) return true;
        return !(this.newMode || (this.prescription && this.prescription.rxNumber));
    }

    get disableInternalTransfer(): boolean {
        if (this.hasClaims) return true;
        if (this.isClaimsLoading) return true;
        if (!this.prescription) return true;
        if (!this.prescription.rxNumber) return true;
        // noinspection RedundantIfStatementJS
        if (isNaN(Number(this.prescription.rfNumber))) return true;
        return false;
    }

    get showSearchBar(): boolean {
        return !(
            this.prescription?.rxNumber ||
            this.newMode ||
            this.fromIngestion ||
            this.isBatchMode ||
            this.routeRxId
        );
    }

    get shouldLockEscriptProcessingStore(): boolean {
        if (this.hasSuperAdminPermission) return false;

        return this.disableFields || !!(this.prescription && this.prescription.rxNumber) || ((!!this.escript.id) && (this.store.id > 0));
    }

    get routeRxId(): string | any {
        let res = null;
        const storeID = Number(this.$route.params.storeID);
        const rxNumber = Number(this.$route.params.rxNumber);
        const rfNumber = Number(this.$route.params.rfNumber);
        const rxId = this.$route.params.rxId;
        if (!isNaN(storeID) && !isNaN(rxNumber) && !isNaN(rfNumber)) {
            res = `${storeID}-${rxNumber}-${rfNumber}`;
        } else if (rxId) res = rxId;
        return res;
    }

    get drugLicenseType(): string {
        return this.drugDetails?.licenseType || "unknown";
    }

    get routeDispenseErrorId(): number | null {
        let res = null;
        if (this.$route.params.dispenseErrorId) {
            res = Number(this.$route.params.dispenseErrorId);
        } else if (this.dispenseError?.id) res = Number(this.dispenseError?.id);
        return res;
    }

    get hasClaims(): boolean {
        return this.claims.length > 0;
    }

    get disablePatient(): boolean {
        if (!!this.prescription.rfNumber) return true;

        if (this.hasSuperAdminPermission) return false;

        if (this.prescription.orders?.some(o => o.status == OrderStatus.Created)) return true;
        if (this.prescription.orders?.some(o => o.status == OrderStatus.Processing)) return true;
        if (this.prescription.orders?.some(o => o.status == OrderStatus.PartialPayment)) return true;
        if (this.prescription.orders?.some(o => o.status == OrderStatus.AwaitingPriorAuthorization)) return true;
        if (this.prescription.orders?.some(o => o.status == OrderStatus.ReadyForPickup)) return true;
        if (this.prescription.orders?.some(o => o.status == OrderStatus.ReturnToSender)) return true;

        return false;
    }

    get isUserPrescriptionAdmin() {
        return this.$permissions.some(p => p.permissionCodeName.toLowerCase() === "Admin/Prescription".toLowerCase());
    }

    // Each branch corresponds to at least one self-gated dropdown item, so adding
    // a permission here without a matching inner item would render an empty menu.
    get hasContactPrescriberAccess() {
        return this.$permissions.some(p =>
            p.permissionCodeName === "Pharmacists/Contact Prescriber" ||
            p.permissionCodeName === "Pharmacists/Fax Rx Clarification Request" ||
            p.permissionCodeName === "Pharmacists/Fax Alternate Rx Request" ||
            p.permissionCodeName === "Admin/Prescription" ||
            p.permissionCodeName === "Super Admin/Prescription" ||
            this.hasProviderCommunicationsSendPermission
        );
    }

    get hasProviderCommunicationsSendPermission() {
        return this.$permissions.some(p =>
            p.permissionCodeName === providerCommunicationsPermissions.send
        );
    }

    get hasRxClarificationAccess() {
        return this.$permissions.some(p =>
            p.permissionCodeName === "Pharmacists/Contact Prescriber" ||
            p.permissionCodeName === "Pharmacists/Fax Rx Clarification Request" ||
            p.permissionCodeName === "Admin/Prescription" ||
            p.permissionCodeName === "Super Admin/Prescription"
        );
    }

    get hasAlternateRxAccess() {
        return this.$permissions.some(p =>
            p.permissionCodeName === "Pharmacists/Contact Prescriber" ||
            p.permissionCodeName === "Pharmacists/Fax Alternate Rx Request" ||
            p.permissionCodeName === "Admin/Prescription" ||
            p.permissionCodeName === "Super Admin/Prescription"
        );
    }

    get notSelectableStatus(): RefillStatusCode[] {
        const baseStatuses: RefillStatusCode[] = [
            RefillStatusCode.TransferIn,
            RefillStatusCode.Canceled,
        ];

        // TransferOut is never user-selectable from the dropdown (set by transfer flows
        // or the Asembia return action). Only discard it when the Rx isn't already in
        // that state — otherwise the selector renders blank instead of showing the
        // current status. Same pattern as Void / Discontinued below.
        if (this.prescription.status !== RefillStatusCode.TransferOut) {
            baseStatuses.push(RefillStatusCode.TransferOut);
        }

        const hasVoidPermission = this.$permissions.some(p =>
            p.permissionCodeName === "Pharmacists/Void Prescription"
        );

        const hasDiscontinuePermission = this.$permissions.some(p =>
            p.permissionCodeName === "Pharmacists/Discontinue Prescription"
        );

        if (!hasVoidPermission && this.prescription.status !== RefillStatusCode.Void) {
            baseStatuses.push(RefillStatusCode.Void);
        }

        if (!hasDiscontinuePermission && this.prescription.status !== RefillStatusCode.Discontinued) {
            baseStatuses.push(RefillStatusCode.Discontinued);
        }

        // Hub mode with inactive drug: only allow OnHold, Void, and TransferOut
        if (this.isHubMode && this.isDrugActive === false) {
            const allowedStatuses = [
                RefillStatusCode.OnHold,
                RefillStatusCode.Void,
                RefillStatusCode.TransferOut,
            ];
            const allStatuses = Object.values(RefillStatusCode).filter(v => typeof v === 'number') as RefillStatusCode[];
            for (const status of allStatuses) {
                if (!allowedStatuses.includes(status) && !baseStatuses.includes(status)) {
                    baseStatuses.push(status);
                }
            }
        }

        return baseStatuses;
    }

    get prescriptionFormState(): PrescriptionFormState {
        return {
            // Prescription entity (property mutations propagate via reference)
            // Note: store, patient, doctor, program, drug are passed as separate .sync props
            prescription: this.prescription,
            originPharmacy: this.originPharmacy,

            // Enum code sets
            originCodes: this.originCodes,
            dawCodes: this.dawCodes,
            deliveryCodes: this.deliveryCodes,
            treatmentTypeCodes: this.treatmentTypeCodes,
            statusCodes: this.statusCodes,
            notSelectableStatus: this.notSelectableStatus,
            applicableSubStatuses: this.applicableSubStatuses,

            // Flags
            disableFields: this.disableFields,
            disableStatusFields: this.disableStatusFields,
            disableBillingComponent: this.disableBillingComponent,
            disableQuantity: this.disableQuantity,
            disableRefills: this.disableRefills,
            disableProgramChange: this.disableProgramChange,
            disablePatient: this.disablePatient,
            shouldLockEscriptProcessingStore: this.shouldLockEscriptProcessingStore,
            newMode: this.newMode,
            fromIngestion: this.fromIngestion,
            newRefillMode: this.newRefillMode,
            showDispensingFields: this.showDispensingFields,
            rxWasBilled: this.rxWasBilled,
            requireNextTouchDateOnHold: this.requireNextTouchDateOnHold,
            minNextTouchDate: this.minNextTouchDate,

            // Display values
            storeLocationInformation: this.storeLocationInformation,
            patientDOB: this.patientDOB,
            isNPIInvalid: this.isNPIInvalid,
            showDrugChange: this.showDrugChange,
            sigIsSmartSig: this.sigIsSmartSig,
            transferMyRxStatus: this.transferMyRxStatus,
            fillsLeft: this.fillsLeft,
            doctorProgram: this.doctorProgram,
            validationClasses: this.validationClasses,

            // Drug details
            drugDetails: this.drugDetails,
            drugNdc: this.drugNdc,
            drugPackageSize: this.drugPackageSize,
            drugStatus: this.drugStatus,
            drugLastFilled: this.drugLastFilled,
            drugInStock: this.drugInStock,
            drugOutOfStock: this.drugOutOfStock,
            drugInventory: this.drugInventory,
            drugAsDrug: this.drugAsDrug,
            imageURLs: this.imageURLs,
            imageError: this.imageError,
            loadingDrugDetails: this.loadingDrugDetails,
            errorLoadingDrugDetails: this.errorLoadingDrugDetails,
            equivalentDrugs: this.equivalentDrugs,

            // Search params
            prescriptionDrugSearchParams: this.prescriptionDrugSearchParams,
            drugSearchParams: this.drugSearchParams,
            searchStoreGetParams: this.searchStoreGetParams,
            searchProgramGetParams: this.searchProgramGetParams,
            autoselectStore: this.autoselectStore,
            autoselectProgram: this.autoselectProgram,
            patientPrescribers: this.patientPrescribers,
            prescriberResultsModifier: this.prescriberResultsModifier,

            // Directions state
            directionsTranslations: this.directionsTranslations,
            selectedTranslation: this.selectedTranslation,
            loadingDirections: this.loadingDirections,

            // Dispensing items
            dispenseItems: this.dispenseItems,

            // Callbacks
            patientUpdate: (value: any) => this.patientUpdate(value),
            doctorUpdate: (value: any) => this.doctorUpdate(value),
            supervisingPhysicianUpdate: (value: any) => this.supervisingPhysicianUpdate(value),
            programUpdate: (value: any) => this.programUpdate(value),
            changeDispense: (num: number) => this.changeDispense(num),
            updateDispenseItem: (field: string, index: number, value: any) => this.updateDispenseItem(field, index, value),
            startEditPatient: () => this.startEditPatient(),
            openRxHistory: () => this.openRxHistory(),
            onShowPatientForm: () => { this.showPatientForm = true; },
            onShowDoctorForm: () => { this.doctorFormMode = "create"; },
            onEditDoctorForm: () => { this.doctorFormMode = "edit"; },
            onShowSupervisingPhysicianForm: () => { this.supervisorFormMode = "create"; },
            onEditSupervisingPhysicianForm: () => { this.supervisorFormMode = "edit"; },
            onShowPharmacyForm: () => { this.showPharmacyForm = true; },
            onSelectedTranslationChange: (value: string) => { this.selectedTranslation = value; },
            translateDirections: () => this.translateDirections(),
            handleDaysSupplyCalculated: (value: number) => this.handleDaysSupplyCalculated(value),
            equivalentDrugsSearched: (drugs: any) => this.equivalentDrugsSearched(drugs),
            onImageErrorChange: (value: boolean) => { this.imageError = value; },
            onOriginPharmacyChange: (value: PharmacyRecord | null) => {
                this.originPharmacy = value || new PharmacyRecord();
                this.prescription.originPharmacyId = value?.id;
            },
        };
    }

    // Cached results for the two field gates. Each is a computed, so disableFields
    // and its debug reason share ONE evaluation of computeDisableFields instead of
    // running the whole gate chain twice per invalidation.
    private get disableFieldsResult(): { disabled: boolean; reason: string } {
        return this.computeDisableFields(false);
    }

    private get disableStatusFieldsResult(): { disabled: boolean; reason: string } {
        return this.computeDisableFields(this.canEditBilledStatus);
    }

    get disableFields(): boolean {
        return this.disableFieldsResult.disabled;
    }

    // Separate gate for just the Status/SubStatus dropdowns. Identical to disableFields,
    // except the "Edit Billed Prescription Status" permission relaxes the active-claim
    // block (and only that block) so a billed Rx can have its status changed while it is
    // still unshipped and unverified.
    get disableStatusFields(): boolean {
        return this.disableStatusFieldsResult.disabled;
    }

    // Pure debug-only reasons for the field gates (DebugViewComponent). Each mirrors
    // its own gate, so the status-field reason no longer drifts to the plain
    // disableFields reason when canEditBilledStatus relaxes the active-claim block.
    get disableFieldsReason(): string {
        return this.disableFieldsResult.reason;
    }

    get disableStatusFieldsReason(): string {
        return this.disableStatusFieldsResult.reason;
    }

    get hasEditBilledStatusPermission(): boolean {
        return this.$permissions.some(p => p.permissionCodeName.toLowerCase() === "Processing/Edit Billed Prescription Status".toLowerCase());
    }

    // The "Edit Billed Prescription Status" permission only applies to unshipped,
    // unverified prescriptions. Existing PV2/shipped gates remain in force for everyone.
    get canEditBilledStatus(): boolean {
        if (!this.hasEditBilledStatusPermission) return false;
        if (this.prescription.hasBeenShipped || this.prescription.hasBeenVerified) return false;
        return true;
    }

    private computeDisableFields(allowBilledStatusEdit: boolean): { disabled: boolean; reason: string } {
        if (this.hasSuperAdminPermission) return { disabled: false, reason: "" };
        const lockedForUser = !this.prescriptionLock;
        const noEntityLoaded = !(this.prescription && this.prescription.rxNumber);
        const isCanceled = this.prescription.status == RefillStatusCode.Canceled;
        let ManagerCond = false;
        if (!this.fromIngestion) {
            let dateNow: Date = new Date();
            let dateFillDate: Date = new Date(this.prescription.fillDate);
            let timeInMilliseconds: number = dateNow.getTime() - dateFillDate.getTime();
            let daysBetweenDates: number = Math.ceil(timeInMilliseconds / (1000 * 60 * 60 * 24));
            ManagerCond =
                !this.isUserPrescriptionAdmin &&
                daysBetweenDates > 15 &&
                this.prescription.status != RefillStatusCode.OnHold &&
                this.prescription.status != RefillStatusCode.AwaitingInsurance;
        }

        if (((lockedForUser || noEntityLoaded) && !this.newMode && !this.fromIngestion)) {
            return { disabled: true, reason: "((lockedForUser || noEntityLoaded) && !this.newMode && !this.fromIngestion)" };
        }

        if (this.hasClaims && !allowBilledStatusEdit) {
            return { disabled: true, reason: "this.hasClaims" };
        }
        if (isCanceled) {
            return { disabled: true, reason: "isCancelled" };
        }
        if (!this.prescription.isActive) {
            return { disabled: true, reason: "!this.prescription.isActive" };
        }
        if (this.prescription.isRx30BeforeApril && !this.newRefillMode) {
            return { disabled: true, reason: "this.prescription.isRx30BeforeApril && !this.newRefillMode" };
        }
        if (ManagerCond) {
            return { disabled: true, reason: "ManagerCond" };
        }

        return { disabled: false, reason: "" };
    }

    get disableRefills() {
        if (this.hasSuperAdminPermission) return false;
        return this.disableFields || (this.hasShipped && this.prescription.rfNumber != 0);
    }

    get disableQuantity() {
        if (this.hasSuperAdminPermission) return false;
        if (this.disableFields) return true;
        return !!this.prescription.rfNumber;
    }

    // Cached result so the gate and its debug reason share one evaluation of
    // computeDisableBillingComponent rather than running it twice per render.
    // Despite the name this flag also gates Directions/SIG and Partner Fill # in
    // PrescriptionFormFields, so it never honors Bill After PV2: editing the
    // directions on a verified fill would let the stored SIG diverge from the
    // label the pharmacist checked, and nothing clears PV2 on save.
    private get disableBillingComponentResult(): { disabled: boolean; reason: string } {
        return this.computeDisableBillingComponent(false);
    }

    // Same gate, but for the insurance billing panel only — the one consumer that
    // Bill After PV2 is meant to unlock. The permission is withheld once the fill
    // has left the pharmacy: hasBeenShipped matches only the Shipped status, so a
    // returned order would otherwise slip past the shipped branch below. A will-call
    // fill awaiting pickup is still billable.
    private get disableInsuranceBillingResult(): { disabled: boolean; reason: string } {
        const allowBillAfterPV2 = this.hasBillAfterPV2Permission && !this.prescription.hasBeenShippedOrReturned;
        return this.computeDisableBillingComponent(allowBillAfterPV2);
    }

    get disableBillingComponent(): boolean {
        return this.disableBillingComponentResult.disabled;
    }

    get disableInsuranceBilling(): boolean {
        return this.disableInsuranceBillingResult.disabled;
    }

    // Pure debug-only reason for the billing gate (DebugViewComponent).
    get disableBillingReason(): string {
        return this.disableBillingComponentResult.reason;
    }

    // Pure debug-only reason for the insurance billing panel gate.
    get disableInsuranceBillingReason(): string {
        return this.disableInsuranceBillingResult.reason;
    }

    private computeDisableBillingComponent(allowBillAfterPV2: boolean): { disabled: boolean; reason: string } {
        if (this.hasSuperAdminPermission) return { disabled: false, reason: "" };

        if (!this.prescription.isActive && !this.newMode) {
            return { disabled: true, reason: "(!this.prescription.isActive && !this.newMode)" };
        }
        if (this.prescription.isRx30BeforeApril && !this.newRefillMode) {
            return { disabled: true, reason: "(this.prescription.isRx30BeforeApril && !this.newRefillMode)" };
        }
        const notRefillable = !this.allowNewRefill && this.newRefillMode;
        if (notRefillable) {
            return { disabled: true, reason: "notRefillable" };
        }
        if (this.statusCode == this.statusCodes.OnHold) {
            return { disabled: true, reason: "this.statusCode == this.statusCodes.OnHold" };
        }
        if (this.isOutOfState) {
            return { disabled: true, reason: "this.isOutOfState" };
        }
        if (this.statusCode == this.statusCodes.AwaitingInsurance) {
            return { disabled: true, reason: "this.statusCode == this.statusCodes.AwaitingInsurance" };
        }
        if (this.prescription.hasBeenShipped && !this.newRefillMode) {
            return { disabled: true, reason: "this.prescription.hasBeenShipped && !this.newRefillMode" };
        }
        // A completed PV2 locks billing unless the caller opted into Bill After PV2.
        // Shipped prescriptions stay locked either way, above.
        if (this.prescription.hasBeenVerified && !this.newRefillMode && !allowBillAfterPV2) {
            return { disabled: true, reason: "this.prescription.hasBeenVerified && !this.newRefillMode && !allowBillAfterPV2" };
        }
        if (this.isOlderThan14Days) {
            return { disabled: true, reason: "this.isOlderThan14Days" };
        }

        return { disabled: false, reason: "" };
    }

    get hasBillAfterPV2Permission() {
        return this.$permissions.some(p => p.permissionCodeName.toLowerCase() === "Patient Dashboard/Bill After PV2".toLowerCase());
    }

    get disableProgramChange(): boolean {
        if (this.hasSuperAdminPermission) return false;

        return this.disableFields || (this.programSetFromTransfer && !this.hasOverrideTransferProgramPermission);
    }

    get hasOverrideTransferProgramPermission() {
        return this.$permissions.some(p => p.permissionCodeName.toLowerCase() === "Processing/Override Transfer Program".toLowerCase());
    }

    get hasShipped(): boolean {
        return !!(this.prescription && this.prescription.rxNumber && !this.prescription.rfNumber);
    }

    get fillsLeft(): number {
        if (!this.prescription) return 0;
        return this.prescription.refills;
        //if (!this.prescription.rfNumber) return this.prescription.refills;
        //return this.prescription.refills - this.prescription.rfNumber;
    }

    get hasHardcopyPermission() {
        return this.$permissions.some(p => p.permissionCodeName === "Maintenance/Hardcopy Note");
    }

    get hasEditImagePermission() {
        return this.$permissions.some(p => p.permissionCodeName === "Maintenance/Edit Image");
    }

    get hasEditScriptPermission() {
        return this.$permissions.some(p => p.permissionCodeName === "Patient Dashboard/Edit Script");
    }

    get hasImageEditOrAnnotatePermission() {
        return this.hasHardcopyPermission || this.hasEditImagePermission;
    }

    // Unsaved drafts keep showing the eScript they were typed from; a saved Rx without the link must not.
    get displayEscriptID(): number | null {
        if (this.prescription?.escriptID) return this.prescription.escriptID;
        if (this.prescription?.rxNumber) return null;
        return Number(this.savedEscriptID) || null;
    }

    // Whether a document exists to annotate. Follows the real link, never the display fallback:
    // stamping a note against a merely-displayed eScript would burn it onto the prescription that
    // still owns it. RxImagePanel owns the matching annotationImageId/annotationEscriptId targeting.
    get canAnnotate(): boolean {
        return !!(this.prescription?.imageID || this.prescription?.escriptID);
    }

    // An eScript is annotated through its own document, so it needs no imageID; a hardcopy does.
    get canEditImage(): boolean {
        if (!this.hasImageEditOrAnnotatePermission) return false;
        if (this.displayEscriptID) return !this.isCancelRxActive && this.canAnnotate;
        return !!this.prescription?.imageID;
    }

    get canRedrawEscript() {
        return this.$permissions.some(p => p.permissionCodeName === "Patient Dashboard/Request New Rx");
    }

    get isCancelRxActive(): boolean {
        return !!this.cancelRxEscriptID;
    }

    // Pure mapping of the reactive form state into the billing-validation input.
    // The RAW disableFutureWrittenDates flag is passed through; the super-admin
    // composition lives inside validateForBilling, not here.
    get billingValidationInput(): BillingValidationInput {
        return {
            prescription: {
                storeID: this.prescription.storeID,
                patientID: this.prescription.patientID,
                packageID: this.prescription.packageID,
                prescriberID: this.prescription.prescriberID,
                supervisingPhysicianId: this.prescription.supervisingPhysicianId,
                programID: this.prescription.programID,
                daySupply: this.prescription.daySupply,
                quantity: this.prescription.quantity,
                dispensed: this.prescription.dispensed,
                originCode: this.prescription.originCode,
                refills: this.prescription.refills,
                remainingQuantity: this.prescription.remainingQuantity,
                rfNumber: this.prescription.rfNumber,
                fillDate: this.prescription.fillDate,
                effectiveDate: this.prescription.effectiveDate,
                directions: this.prescription.directions,
            },
            enableSupervisingPhysician: this.enableSupervisingPhysician,
            doctorRequiresSupervisingPhysician: !!this.doctor?.requiresSupervisingPhysician,
            originCode: this.originCode,
            writtenDate: this.writtenDate,
            expiration: this.expiration,
            initialDispensedQty: this.initialDispensedQty,
            dispensedQuantity: this.dispensedQuantity,
            statusCode: this.statusCode,
            blockFutureWrittenDates: this.disableFutureWrittenDates,
            hasSuperAdminPermission: this.hasSuperAdminPermission,
            enforceEscriptEffectiveDate: this.enforceEscriptEffectiveDate,
            allowNewRefill: this.allowNewRefill,
            newRefillMode: this.newRefillMode,
            packageSizeMultipleInvalid: this.isPackageSizeMultipleInvalid(),
            requireDirectionsAtEntry: this.requireDirectionsAtEntry,
            npiInvalid: this.isNPIInvalid,
            isDrugActive: this.isDrugActive,
            isHubMode: this.isHubMode,
            requireNextTouchDateOnHold: this.requireNextTouchDateOnHold,
            nextTouchDateManualOverride: this.nextTouchDateManualOverride,
            nextTouchDateLocal: this.nextTouchDateLocal,
            now: new Date(),
        };
    }

    get billingValidationResult(): BillingValidationResult {
        return validateForBilling(this.billingValidationInput);
    }

    // Thin adapter: verdict only, no side effects. The two getter-owned
    // validationClasses keys are merged by the transition watcher
    // applyBillingValidationClasses (see below).
    get validFormForBilling(): boolean {
        return this.billingValidationResult.isValid;
    }

    // Field list for the "check the next fields" save-blocked modal. Derived
    // synchronously from the same pure verdict as validFormForBilling, so a
    // same-tick programmatic save can never read a stale list latched a microtask
    // behind the verdict.
    get fieldsPending(): string {
        return this.billingValidationResult.fieldsPending;
    }

    get validForm() {
        // Process validation
        const isOnHold = this.statusCode == RefillStatusCode.OnHold && this.substatusCode != null;
        const isAwaitingStatus = this.statusCode == RefillStatusCode.AwaitingInsurance;
        const isVoid = this.statusCode == RefillStatusCode.Void;
        const isDiscontinued = this.statusCode == RefillStatusCode.Discontinued;
        const isFillDateInTheFuture =
            moment(this.prescription.fillDate).isAfter(moment().toDate(), "day") &&
            moment(this.prescription.fillDate).isBefore(this.expiration, "day");

        const processIsValid =
            this.hasClaims ||
            isOnHold ||
            isVoid ||
            isDiscontinued ||
            isFillDateInTheFuture ||
            (this.prescription.isCashedOut && this.statusCode != RefillStatusCode.OnHold) ||
            isAwaitingStatus;

        //Whole validation
        return (
            this.validFormForBilling &&
            processIsValid &&
            (!this.prescription?.isRx30BeforeApril || this.newRefillMode) &&
            (this.isDrugActive || this.isHubMode)
        );
    }

    get isLastFill(): boolean {
        if (!this.lastFill.hasFullId) return true;
        return this.lastFill?.rxID == this.prescription?.rxID || this.newRefillMode;
    }

    get allowNewRefill(): boolean {
        if (this.patient?.deceased) return false;
        return !!(this.prescription?.isRefillable && this.isLastFill); //&& !this.refillBatchMode && !this.newRefillMode
    }

    get notAllowNewRefill(): boolean {
        return !this.allowNewRefill;
    }

    get validBillingInformation() {
        const isFillDateInTheFuture =
            moment(this.prescription.fillDate).isAfter(moment().toDate(), "day") &&
            moment(this.prescription.fillDate).isBefore(this.expiration, "day");

        return (
            this.hasClaims ||
            this.prescription.isCashedOut ||
            this.prescription.status == RefillStatusCode.Void ||
            this.prescription.status == RefillStatusCode.Discontinued ||
            this.prescription.status == RefillStatusCode.AwaitingInsurance ||
            (this.prescription.status == RefillStatusCode.OnHold &&
                this.prescription.subStatus != null) ||
            isFillDateInTheFuture
        );
    }

    // "Billed" covers every adjudicated status except Rejected — the payer establishes a date of service
    // on all of them — and only while the claim still stands, since a reversal releases that date.
    get rxWasBilled(): boolean {
        return !!this.claims?.some((cl) => !cl.reversed && cl.transactionStatus != "Rejected");
    }

    get hasActiveClaims(): boolean {
        return !!this.claims?.some((cl) => !cl.reversed);
    }

    get isMultiFill(): boolean {
        return (this.dispensedQuantity || 0) > (this.prescription?.quantity || 0);
    }

    get isOutOfState(): boolean {
        if (!this.patient?.addressState || !this.store?.addressState) return false;
        if (this.patient.addressState == this.store.addressState) return false;
        return !this.store.allowedStates.some(s => this.patient.addresses.some(a => a.state?.toUpperCase().trim() == s));
    }

    get showAlertIsOutOfState(): boolean {
        return this.isOutOfState;
    }

    get isHubMode(): boolean {
        return !!(this.store?.capabilities & StoreCapabilities.HubMode);
    }

    get prescriptionDrugSearchParams(): Record<string, any> {
        if (this.isHubMode) {
            return { onlyActive: false, allSources: true };
        }
        return {};
    }

    get isDrugActive(): boolean | null {
        if (!this.drug?.activeStatus) return null;
        return (
            this.drug?.activeStatus === DrugStatus.DrugStatusEnum.Active ||
            this.drug?.activeStatus === DrugStatus.DrugStatusEnum.ActiveAndPreferred
        );
    }

    get showInactiveDrugAlert(): boolean {
        return this.isDrugActive === false;
    }

    get sigIsSmartSig(): boolean {
        if (!this.escript?.smartSig) return false;
        return this.prescription?.directions === this.escript?.smartSig?.sigText;
    }

    get showRefillAuthRequest(): boolean {
        //If we can't fill the next fill, allow a refill auth to go.
        return (
            (this.prescription.remainingQuantity || 0) < (this.prescription.dispensed || 0) * 2 ||
            this.isDrugExpired
        );
    }

    get willAutorefill(): boolean {
        //Is subscribed to autorefill, the current date is more/equals than next process date and this page is in refill mode.
        let nextProcessDateValid = true;
        if (this.autoRefillSubscription?.nextProcessDate) {
            //nextProcessDateValid = moment().isSameOrAfter(this.autoRefillSubscription.nextProcessDate, 'day');
        }
        return !!(this.autoRefillSubscription?.id && (this.newRefillMode || this.currentBatchItemIsExistingFill) && nextProcessDateValid);
    }

    get isDrugExpired() {
        if (!this.expiration) return false;
        // Show refill auth button if prescription expires within 90 days or is already expired
        const expirationDate = moment(this.expiration);
        const daysUntilExpiration = expirationDate.diff(moment(), 'days');
        return daysUntilExpiration <= 90;
    }

    get isOlderThan14Days(): boolean {
        // Users with Admin/Prescription permission can edit scripts regardless of age
        if (this.isUserPrescriptionAdmin || this.newRefillMode) return false;

        const lastFillDate = moment(this.prescription.fillDate);
        const daysOld = moment().diff(lastFillDate, 'days');
        // scripts 14 or more days old require a prescription admin to do adjustments.
        return daysOld >= 14;
    }

    get showAgeAlert(): boolean {
        return this.isOlderThan14Days;
    }

    private onHoldSubStatuses: Array<{ value: number, label: string, active: boolean, offsetValue?: number, offsetUnit?: string }> = [];
    private discontinuedSubStatuses: Array<{ value: number, label: string, active: boolean }> = [];

    get applicableSubStatuses() {
        return this.statusCode == RefillStatusCode.OnHold ? this.onHoldSubStatuses : this.discontinuedSubStatuses;
    }

    get minNextTouchDate() {
        return moment().format("YYYY-MM-DDTHH:mm");
    }

    // The page's configuration loader: the typed feature-flags POST plus the sub-status config.
    // Both are fire-and-forget so the flags still resolve async after the first render, exactly
    // as before (getters flip once the POST lands).
    loadConfiguration(): void {
        void this.loadConfigFeatureFlags();
        void this.loadSubStatusConfiguration();
    }

    // The raw feature-flags POST, kept as its own method so specs can stub it without mocking axios.
    postConfigValues(): Promise<{ data: PrescriptionConfigFlags }> {
        return Axios.post<PrescriptionConfigFlags>("/api/Configuration/values", PRESCRIPTION_CONFIG_KEYS);
    }

    loadConfigFeatureFlags(): Promise<void> {
        return this.postConfigValues()
            .then(resp => {
                const cfg = resp.data;
                this.showDispensingFields = !cfg.HideDispensingFields;
                this.disableFutureWrittenDates = cfg.disableFutureWrittenDates;
                this.enableEquivalentDrugCheck = cfg.EnableEquivalentDrugCheck;
                this.enforcePackageSizeBilling = cfg.EnforcePackageSizeBilling;
                this.allowAnnotationRevert = cfg.AllowAnnotationRevert;
                this.suppressEscriptDiagnosisAlert = cfg.SuppressEscriptDiagnosisAlert;
                this.enforceEscriptEffectiveDate = cfg.EnforceEscriptEffectiveDate;
                this.requireDirectionsAtEntry = cfg.RequireDirectionsAtEntry;
                this.showCrossPatientHardCopyScripts = cfg.ShowCrossPatientHardCopyScripts;
                this.hideSamePatientHardCopyScripts = cfg.HideSamePatientHardCopyScripts;
                this.promptGenericSubstitutionOnBrand = cfg.PromptGenericSubstitutionOnBrand;
                this.enableSupervisingPhysician = cfg.EnableSupervisingPhysician;
                this.enableRxLevelDiagnosis = cfg.EnableRxLevelDiagnosis;
                this.showMoveToBucketOnProcessing = cfg.ShowMoveToBucketOnProcessing;
                this.moveToBucketDestinationFilter = cfg.MoveToBucketDestinationFilter || "All";
                this.retryPendingSourceFetch();
            })
            .catch(err => console.error('Error fetching configuration:', err));
    }

    async loadSubStatusConfiguration() {
        try {
            const resp = await this.$http.get("/api/Configuration");
            this.onHoldSubStatuses = resp.data.onHoldSubStatuses.map((ss: any) => ({
                value: ss.id,
                label: ss.name,
                active: ss.active,
                offsetValue: ss.offsetValue,
                offsetUnit: ss.offsetUnit,
            }));
            this.discontinuedSubStatuses = resp.data.discontinuedSubStatuses.map((ss: any) => ({
                value: ss.id,
                label: ss.name,
                active: ss.active,
            }));
            this.requireNextTouchDateOnHold = resp.data.requireNextTouchDateOnHold ?? false;
        } catch {
            //ignore
        }
    }

    get prescriptionIdentifier(): string {
        if (!this.prescription) return "";
        if (
            this.prescription.storeID == null ||
            this.prescription.rxNumber == null ||
            this.prescription.rfNumber == null
        ) {
            return "N/A";
        }
        return `${this.prescription.storeID}-${this.prescription.rxNumber}-${this.prescription.rfNumber}`;
    }

    get storeLocationInformation(): string {
        if (!this.store?.id) return "";
        return `${this.store.addressCity}, ${this.store.addressState}`;
    }

    get isNPIInvalid() {
        //Apply only if we are loading Escript or Program Transfer
        const escriptId = this.escriptID || this.savedEscriptID;
        const programTransfer = this.programTransfer || this.savedProgramTransfer;
        if (!escriptId && !programTransfer) return false;

        //Invalid if there is no prescriber specified in escript or if not equal to the selected doctor.
        return !this.escriptPrescriber || this.escriptPrescriber.npiNumber != this.doctor.npiNumber;
    }

    get searchProgramGetParams() {
        const escriptId = this.escriptID || this.savedEscriptID;
        const programTransfer = this.programTransfer || this.savedProgramTransfer;
        const programToAutoselect =
            (Number(escriptId) || 0) > 0 || (Number(programTransfer) || 0)
                ? this.prescription.programID
                : null;
        return {
            active: true,
            flags: "Autoselect Only",
            discard: true,
            forceProgramID: programToAutoselect || 0,
            filterByTeam: true,
        };
    }

    get searchStoreGetParams() {
        return {
            filterByTeam: true,
        };
    }

    get patientDOB(): string {
        if (!this.patient || !this.patient.dateOfBirth) return "";
        return moment(this.patient.dateOfBirth).format("L");
    }

    get escriptAllergyAlert() {
        const escriptId = this.escriptID || this.savedEscriptID;
        return (
            escriptId &&
            this.escriptResponseMessage &&
            this.escriptResponseMessage.allergies?.length > 0
        );
    }

    get escriptDiagnosisAlert() {
        const escriptId = this.escriptID || this.savedEscriptID;
        return (
            escriptId &&
            this.escriptDiagnoses?.length > 0 &&
            !this.suppressEscriptDiagnosisAlert
        );
    }

    get additionalAlerts() {
        const alerts: Alert[] = [];

        if (this.escriptAllergyAlert) {
            const allergyNames = (this.escriptResponseMessage?.allergies ?? [])
                .map(a => a.drugText || a.adverseEventText || a.drugCode)
                .filter(Boolean)
                .join(', ');
            const detail = allergyNames ? `: ${allergyNames}` : '';
            alerts.push({
                name: "escriptAllergy",
                title: "Add Escript Allergies",
                message: `Add allergies found on escript before saving prescription${detail}`,
                icon: "fa fa-plus",
                variant: "red"
            });
        }

        if (this.escriptDiagnosisAlert) {
            const codes = this.escriptDiagnoses.map(d => d.icD10Code).join(', ');
            alerts.push({
                name: "escriptDiagnosis",
                title: "Escript Diagnosis",
                message: `ICD10 codes found on escript: ${codes}`,
                icon: "fa fa-stethoscope",
                variant: "info"
            });
        }

        return alerts;
    }

    get isBatchOver(): boolean {
        return this.batchIndex + 1 > (this.prescriptionsInBatch?.length || 0) - 1;
    }

    get isBatchMode(): boolean {
        return !!this.prescriptionsInBatch?.length;
    }

    get currentBatchItemIsExistingFill(): boolean {
        return this.prescriptionsInBatch?.[this.batchIndex]?.isExistingFill === true;
    }

    created() {
        // Construct here (not as an initialized field) so the orchestrator + ports stay out of
        // Vue's reactivity graph; the page-supplied saveState observable carries template reactivity.
        this.orchestrator = new PrescriptionSaveOrchestrator(buildSavePorts(this as any), this.saveState);
        this.create();
        this.fetchPositiveIdConfig();
    }

    async fetchPositiveIdConfig() {
        const cfg = await PositiveIdService.getConfig();
        this.positiveIdEnabled = cfg.enabled;
        this.positiveIdMethods = cfg.methods;
        this.positiveIdConfigLoaded = true;
    }

    onPositiveIdVerifiedForSave(payload: { verifiedToken: string }) {
        // Hold the token on the page too, so if this resumed save aborts pre-POST the next
        // attempt still sees it (snapshot.heldPositiveIdToken) and skips the modal.
        this.verifiedToken = payload.verifiedToken;
        this.orchestrator.resumeWithPositiveId(payload.verifiedToken);
    }

    onPositiveIdCancelledForSave() {
        this.orchestrator.cancelPending('positive-id');
    }

    // noinspection JSUnusedGlobalSymbols
    mounted() {
        this.mount();
        this.fetchDispenseErrorLabel();

        if (this.$route.query?.scrollTo === 'billing') {
            this.pendingScrollToBilling = true;
        }

        document.addEventListener("keydown", this.preventDefaultForHotkeys);
        document.addEventListener("keyup", this.hotKeysListener);
    }

    // noinspection JSUnusedGlobalSymbols
    beforeDestroy() {
        // Cancel any pending escript request when component is destroyed
        this.fetchEscriptAbortController?.abort();

        if (this.rxFocusReopenedAfterSave || this.prescription.rxID !== this.rxFocusLastRecordedRxId) {
            this.recordRxFocusTime(this.prescription.rxID, PageFocusEventClosedAction.Abandoned);
        }
        this.stopRxFocusTimer();

        document.removeEventListener("keydown", this.preventDefaultForHotkeys);
        document.removeEventListener("keyup", this.hotKeysListener);
    }

    private startRxFocusTimer() {
        this.stopRxFocusTimer();
        this.rxFocusVisibleSeconds = 0;
        this.rxFocusVisibleInterval = window.setInterval(() => {
            if (document.hasFocus() && !document.hidden) {
                this.rxFocusVisibleSeconds++;
            }
        }, 1000);
    }

    private stopRxFocusTimer() {
        if (this.rxFocusVisibleInterval !== null) {
            clearInterval(this.rxFocusVisibleInterval);
            this.rxFocusVisibleInterval = null;
        }
    }

    // Number('') resolves to 0 not NaN, so a missing rxID segment needs an explicit check.
    private parseRxId(rxId: string): { storeID: number, rxNumber: number, rfNumber: number } | null {
        const parts = rxId?.split('-') ?? [];
        if (parts.length !== 3 || parts[2] === '') return null;
        const storeID = Number(parts[0]);
        const rxNumber = Number(parts[1]);
        const rfNumber = Number(parts[2]);
        if (!storeID || !rxNumber || isNaN(rfNumber)) return null;
        return { storeID, rxNumber, rfNumber };
    }

    private recordRxFocusTime(rxId: string, closedAction: PageFocusEventClosedAction) {
        if (this.rxFocusLoadedAt <= 0) return;
        const ids = this.parseRxId(rxId);
        if (!ids) return;

        this.rxFocusLastRecordedRxId = rxId;
        PageFocusEventService.record({
            ...ids,
            pageType: PageFocusEventType.RxDetails,
            closedAction,
            visibleDurationSeconds: this.rxFocusVisibleSeconds,
            totalDurationSeconds: Math.round((performance.now() - this.rxFocusLoadedAt) / 1000),
        });
        this.rxFocusLoadedAt = 0;
    }

    // immediate: true -- a queue-item-sourced new Rx never changes rxID before save, so a plain
    // @Watch would never fire and the timer would never arm; recordRxFocusTime no-ops until resolved.
    @Watch('prescription.rxID', { immediate: true })
    onRxIdChangeForFocusTime(newRxId: string, oldRxId: string) {
        if (oldRxId && oldRxId !== this.rxFocusLastRecordedRxId) {
            this.recordRxFocusTime(oldRxId, PageFocusEventClosedAction.Abandoned);
        }

        this.rxFocusLoadedAt = performance.now();
        this.startRxFocusTimer();
    }

    @Watch('$route')
    async onRouteChange(newRoute: any, oldRoute: any) {
        if (newRoute.name !== oldRoute.name) return;

        const getRouteRxId = (route: any) => {
            const s = Number(route.params.storeID);
            const r = Number(route.params.rxNumber);
            const f = Number(route.params.rfNumber);
            if (!isNaN(s) && !isNaN(r) && !isNaN(f)) return `${s}-${r}-${f}`;
            return route.params.rxId || null;
        };

        const oldRxId = getRouteRxId(oldRoute);
        const newRxId = getRouteRxId(newRoute);

        if (oldRxId !== newRxId && newRxId) {
            this.clearForm();
            // clearForm only releases the registered prescriptionLock — also reap any acquire
            // still in flight so a fast rx→rx switch can't orphan a heartbeat.
            await this.clearAllLocks();
            this.create();
            this.mount();
            if (newRoute.query?.scrollTo === 'billing') {
                this.pendingScrollToBilling = true;
            }
        }
    }

    // One key so a queue advance, which changes 2-3 of these props, re-initializes once.
    get ingestionRouteKey(): string {
        return [this.queueItemID, this.imageID, this.escriptID, this.programTransfer]
            .map(v => encodeURIComponent(v ?? ''))
            .join('|');
    }

    @Watch('ingestionRouteKey')
    onIngestionRouteKeyChange(newKey: string, oldKey: string) {
        this.handleQueryPropsChange(newKey, oldKey);
    }

    handleQueryPropsChange(newKey: string, oldKey = '') {
        console.debug("query props change", oldKey, newKey);

        // Only a prop taking a new value is a new script; a full or partial clear is a hop away.
        const oldSegments = oldKey.split('|');
        const openedDifferentItem = newKey.split('|').some((v, i) => v !== '' && v !== oldSegments[i]);
        if (!openedDifferentItem) {
            return;
        }

        console.log('Route query props changed, reloading prescription data');

        // Clear the form to reset all prescription data (including images)
        // This prevents stale data from previous prescription from sticking around
        this.clearForm(false, false);

        // create()/mount() run in this tick, so the imageID watcher never sees the cleared value.
        this.clearSourceImageId();
        this.prescriptionImagePages = 0;

        // Not awaited: clearAllLocks bumps lockGeneration and detaches this.locks synchronously.
        void this.clearAllLocks();
        this.prescriptionLock = null;

        // Reinitialize with new data
        this.create();
        this.mount();
    }

    preventDefaultForHotkeys(e: KeyboardEvent) {
        if (e.code === "F5") e.preventDefault();
        if (e.altKey && e.key === "b") e.preventDefault();
    }

    hotKeysListener(e: KeyboardEvent) {
        if (e.code === "F5" && this.patient?.id) this.openRxHistory();
        if (e.altKey && e.key === "b") this.insuranceComponent.focus();
    }

    create() {
        console.debug("starting prescription details create");
        // Props are now passed from router, already available as this.queueItemID, etc.
        // They're strings from query params, converted to numbers where needed
        this.fromIngestion = !!this.queueItemID;
        if (this.queueItemID) {
            this.savedIngestionQueueItemID = this.queueItemID;
        }
        if (this.escriptID) {
            this.savedEscriptID = this.escriptID;
        }
        if (this.programTransfer) {
            this.savedProgramTransfer = this.programTransfer;
        }

        if (this.fromIngestion) {
            this.buttonTitles.clear = "";
            this.buttonTitles.cancel = "Back to Ingestion";
            this.buttonTitles.new = "Save and New";
            this.buttonTitles.save = "Save and Finish";
            this.buttonTitles.saveAndStay = "Save";
        } else if (!this.routeRxId && !this.routeDispenseErrorId) {
            // Show "Save and New" for all new prescriptions, not just ingestion queue
            this.buttonTitles.new = "Save and New";
        }

        if (this.routeRxId || this.routeDispenseErrorId) {
            this.getPrescription(this.routeRxId);
            this.buttonTitles = {
                save: "Save Changes",
                clear: "",
                new: "Save and New",
                cancel: "",
            };

            if (this.routeDispenseErrorId) {
                this.buttonTitles.cancel = "Back to " + this.dispenseErrorLabel + " List";
            } else if (this.routeRxId) this.buttonTitles.cancel = "Back";
        }

        if (this.isBatchMode) {
            this.buttonTitles.clear = "";
            this.buttonTitles.cancel = "Next";
            this.buttonTitles.new = "";
            this.buttonTitles.save = "Save and Next";

            if (this.prescriptionsInBatch?.length) this.nextRx();
        }
    }

    mount(fetchQueueItem = true) {
        console.debug("starting prescription details mount");
        this.loadConfiguration();
        if (this.imageID) {
            console.log("create with imageID");
            this.createPrescription(Number(this.imageID));
        }
        if (this.escriptID) {
            console.log("create with escriptID");
            this.savedEscriptID = this.escriptID;
            this.createPrescriptionEscript(Number(this.escriptID));
        }
        if (this.programTransfer) {
            console.log("create with programTransfer");
            this.savedProgramTransfer = this.programTransfer;
            this.createPrescriptionFromTransfer(Number(this.programTransfer));
        }
        if (this.queueItemID) {
            const forItem = this.queueItemID;
            this.savedIngestionQueueItemID = this.queueItemID;
            this.addLockWithURL(`/IngestionQueue/${this.queueItemID}/Lock`, 60000)
                .catch((err) => console.warn("Ingestion queue lock not registered", err));

            if (fetchQueueItem) {
                IngestionQueueService.getById(this.queueItemID)
                    .then((data) => {
                        // A later advance already moved on; applying this would load it under the wrong item.
                        if (this.queueItemID !== forItem) return;
                        this.queueItem = data?.id ? data : null;
                        if (this.queueItem?.rxNumber) {
                            const rxId = `${this.queueItem.storeID}-${this.queueItem.rxNumber}-${this.queueItem.rfNumber}`;
                            this.getPrescription(rxId);
                        }
                    })
                    .catch((err) => {
                        const error = err as AxiosError;
                        if (error.response?.status === 418) {
                            this.showLockedMessage(
                                EntityType.IngestionQueueItem,
                                this.queueItemID!.toString(),
                                `Queue Item ${this.queueItemID}`,
                                error.response.data
                            );
                        } else {
                            console.error("Error while loading item queue data.", {
                                err,
                                response: error.response,
                            });
                        }
                    });
            }
        }
    }

    handlePatientDocSaved() {
        if (this.popQueue()) return;
        this.backToPreviousPage(true);
    }

    // Single source of the back-navigation destination priority (ingestion → dispense-errors →
    // previous), read live so both a manual Back click and the post-save back-navigation agree.
    private get backNavigationDestination(): BackDestination {
        return decideBackDestination({
            fromIngestion: this.fromIngestion,
            hasDispenseErrorRoute: this.routeDispenseErrorId != null,
        });
    }

    backToPreviousPage(skipBillingValidation = false) {
        this.navigateBackTo(this.backNavigationDestination, skipBillingValidation);
    }

    // Execute a back-navigation to an already-decided destination. The destination priority
    // lives in decideBackDestination (pure); this method only performs the routing, including
    // the ingestion billing-information guard.
    navigateBackTo(destination: BackDestination, skipBillingValidation = false) {
        switch (destination) {
            case "ingestion":
                if (this.validBillingInformation || skipBillingValidation) {
                    this.$router.push({ name: "Ingestion" });
                } else {
                    this.$notification(
                        NotificationOptions.notificationOptionsPreset(
                            "Complete billing information before going back to ingestion list.",
                            NotificationOptions.NotificationTypes.danger,
                        ),
                    );
                }
                break;
            case "dispenseErrors":
                this.$router.push({ name: "DispenseErrors" });
                break;
            case "previous":
                if (previousRoute) {
                    this.$router.push(previousRoute);
                } else {
                    this.$router.go(-1);
                }
                break;
        }
    }

    scrollToBillingSection() {
        this.$nextTick(() => {
            const el = this.$refs.insuranceBillingSection as HTMLElement;
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    }

    claimsLoaded(billedClaims: Claim[], rx: Prescription) {
        this.claims = billedClaims;
        // Assign claims to prescription for transfer validation
        this.prescription.claims = billedClaims;
        this.isClaimsLoading = false;
        this.updatePricing(rx);

        // Billing produced a claim — arm the Save-and-New supersede latch on this fill
        // (the pre-bill GetClaims emits empty and must not arm it).
        // Only consume the arm for the exact Rx it was set for: if the Rx was swapped while its
        // GetClaims was in flight the child drops that response, so an unrelated later load must
        // not stamp supersede onto the current (wrong) prescription.
        // Capture identity from rx (the server's echoed prescription), never the live form:
        // the operator may have edited the form during the adjudication round trip, and the
        // latch must hold what the server actually stored for the billed fill.
        // The billing paths emit the raw response body, a plain JSON object with no Prescription
        // prototype — so the computed `rxID` getter is absent and only the lowercase `rxId` shadow
        // is on the wire. Rebuild the id from the primitives every shape carries: the shadow is
        // cleared deliberately on a live instance (prepareNewRxFromBilled) and can be stale.
        const billedRxId = Prescription.createRxId(rx.storeID, rx.rxNumber, rx.rfNumber);
        if (this.billedNewRxAwaitingClaims && this.billedNewRxAwaitingClaimsRxId === billedRxId && billedClaims?.length) {
            this.billedNewRxAwaitingClaims = false;
            this.billedNewRxAwaitingClaimsRxId = null;
            this.supersedeBilledFill = { rxId: billedRxId, ...captureFillIdentity(rx) };
            return;
        }

        if (this.pendingScrollToBilling) {
            this.pendingScrollToBilling = false;
            this.scrollToBillingSection();
        }
    }

    // The latch's three fields must always reset together (form left the billed fill entirely).
    private disarmBilledSupersede() {
        this.supersedeBilledFill = null;
        this.billedNewRxAwaitingClaims = false;
        this.billedNewRxAwaitingClaimsRxId = null;
    }

    onCompoundPresetChanged(presetId: number | null) {
        if (this.prescription) {
            this.prescription.compoundPresetID = presetId;
        }
    }

    updatePricing(rx: Prescription) {
        if (rx?.rxNumber) {
            this.prescription.patientPay = rx.patientPay;
            this.prescription.cost = rx.cost;
            this.prescription.totalPrice = rx.totalPrice;
            this.prescription.tax = rx.tax;
            this.prescription.priceScheduleID = rx.priceScheduleID;
            this.prescription.status = rx.status;
            this.statusCode = rx.status;
        }
    }

    changeDispense(num: number) {
        if ((this.prescription.quantity ?? 0).toString().length > 11) {
            this.prescription.quantity = Number(
                (this.prescription.quantity ?? 0).toString().substring(0, 11),
            );
        }
        if (num.toString().length > 11) {
            num = Number(num.toString().substring(0, 11));
        }
        this.dispensedQuantity = Number(num);
        this.prescription.dispensed = Number(num);
    }

    handleDaysSupplyCalculated(calculatedDaysSupply: number) {
        // Only update days supply if it's empty or zero
        if (!this.prescription.daySupply || this.prescription.daySupply === 0) {
            this.prescription.daySupply = calculatedDaysSupply;
        }
    }

    fetchPrescription(prescription: Prescription) {
        if (prescription.rfNumber || prescription.rfNumber == 0) {
            this.getPrescription(prescription.rxID);
        }
    }

    toggleUrgent() {
        const newUrgent = !this.prescription.isUrgent;
        const rxId = `${this.prescription.storeID}-${this.prescription.rxNumber}-${this.prescription.rfNumber}`;
        PrescriptionService.setUrgent(rxId, newUrgent)
            .then(() => {
                this.prescription.isUrgent = newUrgent;
            })
            .catch(() => {
                this.$notification(NotificationOptions.notificationOptionsPreset("Failed to update urgent status.", NotificationOptions.NotificationType.ERROR));
            });
    }

    rxTransferRefresh(transferredPrescription: Prescription) {
        this.getPrescription(transferredPrescription.rxID).then(
            this.replaceRouterParamsWithCurrentPrescription,
        );
    }

    // noinspection SpellCheckingInspection
    /**
     * rxID: Concatenated Rx Ids in the format StoreID-RxNumber-RfNumber
     * setupRefill: If true, the method will load the prescription for RxID and immediately will set up its refill in the front end ready to be edited and saved.
     */
    getPrescription(rxID: string, setupRefill = false) {
        return new Promise((resolve, reject) => {
            // linked objects will be automatically fetched by the SearchComponent when we set the objects...
            // if we manually fetch them, they are called for twice.
            this.acquirePrescriptionLock(rxID).finally(() => {
                PrescriptionService.getById(rxID)
                    .then((data) => {
                        // Load-race guard: a fetch resolving after the user navigated away (which
                        // clears this.prescription) must not overwrite fresh state or fire follow-ups.
                        if (!this.prescription) return reject();

                        this.hydratePrescription(data);
                        this.loadLastFill();
                        this.loadLatestFillStatus();
                        this.loadStoredPharmacy();
                        this.loadFollowupFetches(setupRefill);

                        resolve(undefined);
                    })
                    .catch((error) => {
                        console.error("Error while getting Prescription", {
                            error,
                            response: error?.response,
                        });
                        reject();
                    });
            });
        });
    }

    // Acquire the per-prescription edit lock; a 418 conflict shows the locked message. Never
    // rejects — the caller's .finally() proceeds to load regardless, matching prior behavior.
    private acquirePrescriptionLock(rxID: string, ignoreFocus = false): Promise<void> {
        return this.addLockWithURL(prescriptionLockUrl(rxID), 60000, ignoreFocus)
            .then((result) => {
                this.prescriptionLock = result;
            })
            .catch((error) => {
                if (error.response && error.response.status == 418) {
                    this.showLockedMessage(
                        EntityType.Prescription,
                        rxID,
                        rxID,
                        error.response.data,
                    );
                }
            });
    }

    // The save endpoint locks the Rx it saved for this user, but the page only releases locks it
    // registered itself (leaving the Rx, Save and New, the next queue item, tab close). A newly created
    // Rx or refill was never registered, so its lock outlived the page until the server lease expired.
    // Re-acquiring a lock this user already holds is a renewal, not a conflict.
    private async registerSavedPrescriptionLock(): Promise<void> {
        if (!this.prescription?.rxNumber) return;

        const rxID = this.prescription.rxID;
        const url = prescriptionLockUrl(rxID);

        // A refill save lands on a new fill, so the fill the page had locked is no longer the one on screen.
        // Never release this Rx's own URL here: that release would race the re-acquire and could clear the save's lock.
        if (this.prescriptionLock && this.prescriptionLock.refreshURL !== url) {
            this.releaseLockAtURL(this.prescriptionLock.refreshURL);
        }

        // Ask the mixin rather than trust prescriptionLock, which a heartbeat 418 release leaves stale.
        this.prescriptionLock = this.getLockAtURL(url) ?? null;
        if (this.prescriptionLock) return;

        // The save already holds this lock server-side, so a blurred window must not skip registering it.
        await this.acquirePrescriptionLock(rxID, true);
    }

    // Assign the fetched prescription and its derived form/entity state (patient, doctor, store,
    // program, drug). Runs only after the load-race guard passes.
    private hydratePrescription(data: any): void {
        this.prescription = new Prescription(data);
        this.disarmBilledSupersede();
        this.setViewValues();

        this.initialDispensedQty = this.prescription.dispensed;

        this.patient.id = this.prescription.patientID;
        this.doctor.id = this.prescription.prescriberID;
        this.supervisingPhysician = this.prescription.supervisingPhysicianId
            ? Object.assign(new Prescriber(), { id: this.prescription.supervisingPhysicianId })
            : null;
        this.store.id = this.prescription.storeID;
        this.program.id = this.prescription.programID || 0;
        this.originalFillDate = this.prescription.fillDate;

        this.enableExpirationDateEstimation = false;

        switch (this.prescription.drugSource) {
            case DrugSource.GsddFederal:
                this.drug = new Drug(this.prescription.packageID);
                break;
            case DrugSource.NonGsddProduct:
                this.drug = new NonGSDDDrug(this.prescription.packageID);
                break;
            case DrugSource.CompoundProduct:
                this.drug = new CompoundDrug(this.prescription.packageID);
                break;
        }
        this.getDrugDetails(this.drug);
    }

    // Follow-up fetch: the patient's last fill for this Rx.
    private loadLastFill(): void {
        PrescriptionService
            .getLastFill(this.prescription.patientID, this.prescription.storeID, this.prescription.rxNumber)
            .then((data) => {
                if (data?.rxNumber > 0) {
                    this.lastFill = new Prescription(data);
                }
            })
            .catch((err) => {
                console.error("Error while getting last fill info", {
                    err,
                    response: err?.response,
                });
                this.$notification(NotificationOptions.error(err));
            });
    }

    // Follow-up fetch: latest non-void fill status — used to disable "New Refill" when an earlier
    // fill is being viewed but the prescription as a whole is in a blocking state.
    private loadLatestFillStatus(): void {
        PrescriptionService
            .getLatestFillStatus(this.prescription.storeID, this.prescription.rxNumber)
            .then((data) => {
                this.latestFillStatus = data;
            })
            .catch((err) => {
                console.error("Error while getting latest fill status", {
                    err,
                    response: err?.response,
                });
            });
    }

    // Follow-up fetch: the origin pharmacy record, when the prescription carries one.
    private loadStoredPharmacy(): void {
        if (this.prescription.originPharmacyId) {
            this.$http.get<PharmacyRecord>(`/api/StoredPharmacy/${this.prescription.originPharmacyId}`)
                .then(pharmRecord => {
                    this.originPharmacy = new PharmacyRecord(pharmRecord.data);
                }).catch(err => this.$notification(NotificationOptions.error(err)));
        }
    }

    // Follow-up fetch: in a refill batch, set up the refill (+ MedSync short-fill + autorefill
    // subscription); otherwise load the dispense-error and escript context.
    private loadFollowupFetches(setupRefill: boolean): void {
        //Setting up RX form in a refill batch
        if (setupRefill) {
            this.fetchEscript();
            if (!this.currentBatchItemIsExistingFill) {
                this.newRefill();
            }

            // Apply MedSync short fill adjustments if provided
            if (this.medSyncShortFillData?.isMedSyncShortFill) {
                this.prescription.daySupply = this.medSyncShortFillData.adjustedDaysSupply;
                this.prescription.dispensed = this.medSyncShortFillData.adjustedDispensed;
                this.dispensedQuantity = this.medSyncShortFillData.adjustedDispensed;
            }

            this.loadAutoRefillSubscription();
        } else {
            this.fetchDispenseError();
            this.fetchEscript();
        }
    }

    // Follow-up fetch: the autorefill subscription for this Rx (refill-batch path only).
    private loadAutoRefillSubscription(): void {
        this.$http
            .get<AutoRefillSubscription>(
                `autorefill/subcriptions-by-rx/${this.prescription.storeID}/${this.prescription.rxNumber}`,
            )
            .then((res) => {
                if (res.data.id) {
                    this.autoRefillSubscription =
                        new AutoRefillSubscription(res.data);
                }
            })
            .catch((err) => {
                console.error("Error while getting autorefill sub data.", {
                    err,
                    response: err?.response,
                });
                this.$notification(NotificationOptions.error(err));
            });
    }

    async fetchEscript() {
        this.fetchEscriptAbortController?.abort();

        let rxId = this.prescription.rxID;
        if (rxId.endsWith("-")) rxId += "0";
        const capturedRxId = rxId;

        this.fetchEscriptAbortController = new AbortController();

        try {
            const data = await EscriptService.getForRx(rxId, this.fetchEscriptAbortController.signal);

            if (data?.eScript) {
                // Validate that the response is still relevant to the current prescription.
                // Prefer matching by escriptID (definitive); fall back to normalized rxID so
                // we don't over-reject when this.prescription was swapped mid-load.
                const currentRxId = this.prescription.rxID;
                const normalizedCurrentRxId = currentRxId.endsWith("-") ? currentRxId + "0" : currentRxId;
                const incomingEscriptId = data.eScript.id;
                const prescriptionEscriptId = this.prescription.escriptID;

                const matchesByEscriptId =
                    prescriptionEscriptId != null && incomingEscriptId === prescriptionEscriptId;
                const matchesByRxId = normalizedCurrentRxId === capturedRxId;

                if (!matchesByEscriptId && !matchesByRxId) {
                    console.warn("Escript response ignored - prescription changed during async request", {
                        requestedRxId: capturedRxId,
                        currentRxId: normalizedCurrentRxId,
                        incomingEscriptId,
                        prescriptionEscriptId,
                    });
                    return;
                }
                this.escript = new Escript(data.eScript);

                // Do not overwrite the prescription's escriptID with a CancelRx escript — that
                // would break pairing logic which relies on escriptID pointing to the originating
                // NewRx/RxRenewalResponse/RxChangeResponse escript. Track the CancelRx escript
                // separately so the Prescription Details page can display its watermarked image
                // as the primary image without breaking pairing.
                if (!data.message?.cancel) {
                    this.prescription.escriptID = data.eScript.id;
                    this.savedEscriptID = data.eScript.id;
                } else {
                    this.cancelRxEscriptID = data.eScript.id;
                }
            }
        } catch (error) {
            if (Axios.isCancel(error)) {
                console.log("Escript request cancelled:", error.message);
                return;
            }
            console.error("Error while getting escript", {
                error,
                response: (error as any)?.response,
            });
        }
    }

    fetchDispenseError() {
        Axios.get(`DispenseError/By-RxId/${this.prescription.rxID}`)
            .then((response) => {
                const findCreatedCallBack = (err: DispenseError) =>
                    err.statusEnum == DispenseError.DispenseErrorStatusEnum.Created;
                const emptyDispenseError = new DispenseError(this.prescription);
                this.dispenseErrors = response.data.map((err: DispenseError) =>
                    Object.assign(new DispenseError(), err),
                );
                if (this.dispenseErrors?.some(findCreatedCallBack)) {
                    this.dispenseError =
                        this.dispenseErrors.find(findCreatedCallBack) || emptyDispenseError;
                } else {
                    this.dispenseError = emptyDispenseError;
                }
            })
            .catch((error) => {
                console.error("Error while getting error", {
                    error,
                    response: error?.response,
                });
            });
    }


    @Watch("searchProgramGetParams") searchProgramParamsChanged() {
        this.programSearchComponent?.reloadResults();
    }

    @Watch("writtenDate") changedWrittenDate(value: string | Date | null) {
        let val = value;
        if (!(val instanceof Date)) val = moment(val).toDate();

        this.prescription.writtenDate = val;

        if (value && this.enableExpirationDateEstimation) {
            this.estimateExpirationBasedOnPackage(this.prescription.packageID, this.prescription.drugSource, val);
        }

        if (!this.enableExpirationDateEstimation) this.enableExpirationDateEstimation = true;
    }

    @Watch("prescription.imageID")
    async fetchPrescriptionImagePages(imageID: number | null | undefined) {
        // Reset page count when imageID becomes null/undefined
        if (!imageID) {
            this.prescriptionImagePages = 0;
            this.clearSourceImageId();
            return;
        }

        // Fetch image information to get the page count and set origin if from fax
        try {
            const imageInfo = await ImageService.getImageInformation(imageID);
            this.prescriptionImagePages = imageInfo?.pageCount ?? 0;

            // If the prescription image was received via fax, set the Origin to Fax
            if (imageInfo?.createdBy === "system/SRFax") {
                this.originCode = OriginCode.Fax;
            }
        } catch (error) {
            // If fetching fails, reset to 0
            this.prescriptionImagePages = 0;
        }

        this.fetchSourceImageId(imageID);
    }

    estimateExpirationBasedOnPackage(packageId: number, source: DrugSource, writtenDate: Date) {
        const addressState = this.store.addressState;
        let estimatedExpirationDate = moment(writtenDate);
        if (packageId && addressState) {
            DrugService.getScheduledExpiration(packageId, source, addressState).then((data) => {
                const deaSchedule = Number(data);
                this.expiration = estimatedExpirationDate.add(deaSchedule, 'days').toDate();
            });
        } else {
            estimatedExpirationDate = estimatedExpirationDate.add(365, "days");
            this.expiration = estimatedExpirationDate.toDate();
        }
    }

    @Watch("store") storeUpdate(value: any) {
        if (!value || !this.prescription) return;
        this.prescription.storeID = value.id;
    }

    patientUpdate(value: any) {
        if (!value || !this.prescription) return;

        this.prescription.patientID = value.id;
        if (
            !this.prescription.deliveryOption &&
            (value.defaultDelivery != undefined || value.defaultDelivery != null) &&
            !this.prescription.rxNumber
        ) {
            this.deliveryCode = value.defaultDelivery;
        }
    }

    @Watch('patient.id')
    async fetchPatientPrescribers() {
        const patientId = this.patient?.id;
        if (!patientId) {
            this.patientPrescribers = [];
            return;
        }
        try {
            const response = await Axios.get(`/api/Prescriber/by-patient/${patientId}`);
            this.patientPrescribers = response.data || [];
        } catch {
            this.patientPrescribers = [];
        }
    }

    prescriberResultsModifier(searchResults: any[], params?: { searchTerm?: string }) {
        const searchTerm = (params?.searchTerm ?? "").trim();
        // While the user is searching, show only API matches — recent prescribers
        // would dilute the results and lead to incorrect selections.
        if (searchTerm) return searchResults;

        if (!this.patientPrescribers.length) return searchResults;

        return [
            { heading: true, label: "Patient's Prescribers" },
            ...this.patientPrescribers,
        ];
    }

    doctorUpdate(value: any) {
        if (!value || !this.prescription) return;

        this.prescription.prescriberID = value.id;
        this.setRecommendedProgram();
    }

    autoselectProgram(programs: Program[]) {
        // Do not autoselect if a program is already set
        if (this.program?.id) return null;
        // Do not autoselect for escript or transfer flows (handled by forceProgramID)
        if (this.escriptID || this.savedEscriptID) return null;
        if (this.programTransfer || this.savedProgramTransfer) return null;
        // Autoselect if only one program is available
        if (programs.length === 1) return programs[0];
        return null;
    }

    programUpdate(value: any) {
        if (!this.prescription) return;
        if (!value?.id) this.program = new Program();

        this.prescription.programID = value?.id;
    }

    @Watch("drugDetails")
    drugDetailsUpdate(value: Drug | null, _oldValue: Drug | null) {
        this.imageURLs = getDrugImageUrls(value?.drugId, value?.source);
        if (!value) return;

        this.drug.ndc = value.ndc;
        if (this.drug instanceof Drug) this.drug.productNameLong = value.productNameLong;
    }

    @Watch("drugDetails")
    @Watch("prescription.dispensed")
    drugOrDispenseUpdate() {
        const cost = this.drugDetails?.vendors?.find((v: any) => v.displayOrder == 1)?.cost ?? 0;
        if (cost) {
            this.prescription.estimatedCost = this.prescription.dispensed * cost;
        }
    }

    private isPackageSizeMultipleInvalid(): boolean {
        if (!this.enforcePackageSizeBilling) return false;
        if (this.statusCode === RefillStatusCode.Void
            || this.statusCode === RefillStatusCode.OnHold
            || this.statusCode === RefillStatusCode.TransferOut
            || this.substatusCode === RxDeniedSubStatusId) return false;
        if (!this.drugDetails) return false;
        const billingUnit = this.drugDetails.billingUnit;
        const packageSize = this.drugDetails.packageSize;
        if (billingUnit?.toLowerCase() === "ea" || !packageSize || packageSize <= 0) return false;
        const dispensed = this.dispensedQuantity;
        if (!dispensed || dispensed <= 0) return false;
        const ratio = dispensed / packageSize;
        return Math.abs(ratio - Math.round(ratio)) > 0.0001;
    }

    @Watch("drug")
    drugUpdate(value: DrugProduct, oldValue: DrugProduct) {
        if (!value || !value.drugId) {
            return;
        }

        // Skip only when the Rx already carries this drug; else re-picking can't repair a cleared packageID.
        const alreadyApplied = +(this.prescription?.packageID ?? 0) === +value.drugId;
        if (
            alreadyApplied &&
            oldValue && oldValue.drugId && +value.drugId == +oldValue.drugId && (value as Drug).productID == (oldValue as Drug).productID
        ) {
            return;
        }

        let packId: number = +value.drugId;
        if ((value as Drug).productID) this.fetchDrugCategory((value as any).productID);

        let drug: DrugProduct;
        switch (value.source) {
            case DrugSource.GsddFederal:
                drug = new Drug(packId, value);
                break;
            case DrugSource.NonGsddProduct:
                drug = new NonGSDDDrug(packId, value);
                break;
            case DrugSource.CompoundProduct:
                drug = new CompoundDrug(packId, value);
                break;
        }

        this.fetchDrug(drug);
        this.fetchDrugAlert(drug);
    }

    get showDeaAlert() {
        const needsDea = this.isScheduleClassDrug;
        const hasDea = !!this.doctor?.deaNumber;
        return needsDea && !hasDea;
    }

    getDrugDetails(drug: DrugProduct) {
        const drugId = drug.drugId;
        this.loadingDrugDetails = true;
        // Share fetchDrug's counter: assigning this.drug below fires the drug watcher,
        // which starts a fetchDrug for the same package. Without a common sequence this
        // method's finally would clear the loading flag while that newer request is
        // still in flight, dropping the spinner with the details panel still empty.
        const seq = ++this.drugFetchSeq;
        DrugService.getDetails(drugId, drug.source)
            .then((data) => {
                if (this.prescription?.packageID == data.drugId) {
                    switch (data.source) {
                        case DrugSource.GsddFederal:
                            this.drug = new Drug(data.drugId, data);
                            break;
                        case DrugSource.NonGsddProduct:
                            this.drug = new NonGSDDDrug(data.drugId, data);
                            break;
                        case DrugSource.CompoundProduct:
                            this.drug = new CompoundDrug(data.drugId, data);
                    }

                    this.drugDetails = data;
                    this.drugDetails.status = this.drug.activeStatus;
                    this.prescription.drugSource = drug.source;

                    DrugService.getInventory(drugId, drug.source)
                        .then((inventory) => {
                            let storeInventories = inventory.filter(
                                (inv) => inv.storeId == this.prescription.storeID,
                            );
                            if (storeInventories.length == 1) {
                                this.drugInventory = storeInventories[0];
                            } else {
                                this.drugInventory = storeInventories.filter(
                                    (inv) => inv.programId == this.prescription.programID,
                                )[0];
                            }
                        })
                        .catch((err) => this.$notification(NotificationOptions.error(err)));
                } else {
                    console.warn(
                        "drug details didn't match the prescription's packageID",
                        data,
                    );
                }
            })
            .catch((error) => {
                console.warn(error);
            })
            .finally(() => {
                // Only the most recent request owns the loading flag.
                if (seq === this.drugFetchSeq) this.loadingDrugDetails = false;
            });
    }

    fetchDrugAlert(drug: DrugProduct) {
        DrugService.getAlert(drug.drugId, drug.source)
            .then((data) => {
                this.drugAlert = data;
            })
            .catch((error) => {
                console.error("Error while getting drug alert", {
                    error,
                    response: error?.response,
                });
            });
    }

    fetchDrug(drug: DrugProduct) {
        const packId = Number(drug.drugId);
        if (this.prescription) {
            this.prescription.packageID = packId;
            this.prescription.drugSource = drug.source;
        }
        //Estimate expiration date only if we are creating a new RX
        if (!this.prescription?.rxNumber) {
            this.estimateExpirationBasedOnPackage(packId, drug.source, moment(this.writtenDate).toDate());
        }

        this.loadingDrugDetails = true;
        this.errorLoadingDrugDetails = false;
        const seq = ++this.drugFetchSeq;
        DrugService.getDetails(drug.drugId, drug.source)
            .then((data) => {
                // Apply the payload whenever it still describes the committed drug, even
                // if a newer request has started since: the data is correct for what is
                // on screen, and discarding it would leave the panel empty if that newer
                // request then fails. The sequence number governs only the loading and
                // error flags below, where latest-wins is what we want.
                if (!this.disableFields && this.prescription?.packageID == data.drugId) {
                    this.drugDetails = data;
                    const deaClass = this.drugDetails.deaClass ? this.drugDetails.deaClass : this.drugDetails.deaClassificationId;
                    this.isScheduleClassDrug = deaClass < 5;
                    this.setRecommendedProgram();
                }
            })
            .catch((error) => {
                if (seq !== this.drugFetchSeq) return;
                console.error("Error while getting drug details", {
                    error,
                    response: error?.response,
                });
                this.errorLoadingDrugDetails = true;
            })
            .finally(() => {
                // Only the latest request owns the loading flag; a superseded request
                // must not clear it out from under the in-flight newer one.
                if (seq === this.drugFetchSeq) this.loadingDrugDetails = false;
            });
    }

    setRecommendedProgram() {
        // Do not attempt to set the recommended program if we've already saved the prescription before
        if (this.prescription?.rxNumber) return;

        // Do not attempt to set the recommended program if we've already set it from the transfer
        if (this.programSetFromTransfer) return;

        const programPrescriberId = this.doctor?.programId || 0;
        const drugProgramId = this.drugDetails?.program?.id || 0;

        //Prescriber program takes precedence over the drug program
        const programId = programPrescriberId || drugProgramId;

        // Do not clear an existing program selection if there is no recommendation
        if (!programId) return;

        if (!this.program) this.program = new Program();

        // Skip reassignment when the recommendation already matches the current
        // selection, otherwise mutating program.id re-triggers a forceReload.
        if (Number(this.program.id) === Number(programId)) return;

        this.program.id = programId;
    }

    get canCreateRefill() {
        if (!this.prescription) return false;
        if (this.prescription.status == RefillStatusCode.TransferOut) return false;
        if (this.prescription.isExpired) return false;
        if (this.prescription.status == RefillStatusCode.OnHold && this.prescription.subStatus == RxDeniedSubStatusId) return false;
        if (this.prescription.status == RefillStatusCode.Discontinued) return false;
        if (!this.prescription.rfNumber && this.prescription.status == RefillStatusCode.Void) return false;

        // Mirror the backend gate: if a later fill exists and is in a
        // blocking state, the user must not be able to create a refill
        // even when viewing an earlier still-Refillable fill.
        if (this.latestFillStatus) {
            const latest = this.latestFillStatus;
            if (latest.status == RefillStatusCode.TransferOut) return false;
            if (latest.status == RefillStatusCode.Discontinued) return false;
            if (latest.status == RefillStatusCode.Canceled) return false;
            if (latest.status == RefillStatusCode.OnHold && latest.subStatus == RxDeniedSubStatusId) return false;
        }

        return true;
    }

    /**
     * Prepares the form to submit a new refill for the current prescription.
     */
    newRefill() {
        // Check if the patient is deceased
        if (this.patient?.deceased) {
            this.$notification(NotificationOptions.error('Cannot create refills for deceased patients.'));
            return;
        }

        if (!this.canCreateRefill) return;

        const nextRfNumber = (this.prescription.rfNumber || 0) + 1;
        this.nextRefillRxId = Prescription.createRxId(
            this.prescription.storeID,
            this.prescription.rxNumber,
            nextRfNumber,
        );
        this.newRefillMode = true;

        this.prescription.rfNumber = undefined;
        this.clearBilledFillIdentity();
        this.prescription.fillDate = this.clampFillDateToEffective(moment().startOf("day").toDate());

        const comp = this.$refs.insuranceComponent as InsuranceBillingComponent;
        if (comp) comp.fetchPatientClaims();
    }

    unVoid() {
        this.$bvModal
            .msgBoxConfirm(
                "Prescription status will be set back to Refillable and save changes if you confirm. Are you sure you want to unVoid this prescriptions?",
                {
                    title: "Confirm",
                    size: "sm",
                    buttonSize: "sm",
                    okVariant: "danger",
                    okTitle: "YES",
                    cancelTitle: "NO",
                    footerClass: "p-2",
                    hideHeaderClose: false,
                    centered: true,
                },
            )
            .then((value) => {
                if (!value) return;
                this.statusCode = RefillStatusCode.Refillable;
                this.save();
            })
            .catch((err) => {
                console.error("Error caught on unVoid button.", err);
            });
    }


    cancelPV2() {
        if (this.prescription.hasBeenShipped) {
            this.$notification(
                NotificationOptions.notificationOptionsPreset(
                    "Cannot cancel PV2 on shipped prescription",
                    NotificationOptions.NotificationTypes.danger,
                ),
            );
            return;
        }

        this.$bvModal
            .msgBoxConfirm(
                "By Cancelling PV2, any previous PV2 done for this RX will cancelled and inventory will be put back to stock. Are you sure?",
                {
                    title: "Confirm",
                    size: "sm",
                    buttonSize: "sm",
                    okVariant: "danger",
                    okTitle: "YES",
                    cancelTitle: "NO",
                    footerClass: "p-2",
                    hideHeaderClose: false,
                    centered: true,
                },
            )
            .then((value) => {
                if (!value) return;

                const rxId = this.prescription.rxID;
                if (!rxId) return;

                PrescriptionService.cancelPV2(rxId)
                    .then((result) => {
                        if (result.cancelled) {
                            this.prescription.pV2ID = null;
                            this.$notification(
                                NotificationOptions.notificationOptionsPreset(
                                    "PV2 was cancelled.",
                                    NotificationOptions.NotificationTypes.success,
                                ),
                            );
                        } else {
                            this.$notification(
                                NotificationOptions.errorSaveNotificationPreset("PV2 cancellation"),
                            );
                        }
                    })
                    .catch((error) => {
                        console.error("Error while cancelling PV2", {
                            error,
                            response: error?.response,
                        });
                        this.$notification(
                            NotificationOptions.errorSaveNotificationPreset(
                                "PV2 cancellation",
                                error,
                            ),
                        );
                    });
            })
            .catch((err) => {
                console.error("Error caught on cancel PV2 button.", err);
            });
    }

    newPrescription() {
        // Check if the patient is deceased
        if (this.patient?.deceased) {
            this.$notification(
                NotificationOptions.error('Cannot create new prescriptions for deceased patients.')
            );
            return;
        }

        this.newMode = true;
        this.createPrescription();
    }

    createPrescription(imageID?: number) {
        this.prescription = new Prescription();
        // Reset the display-bound Program/Drug objects too. The fresh Prescription zeroes
        // programID/packageID, but the search boxes bind to these separate objects and their
        // update handlers only fire on a value change, so without this they keep showing the
        // previous Rx while validation sees the empty IDs (mirrors clearPrescription()).
        this.program = new Program();
        this.drug = new Drug();
        // Clear the drug info panel (NDC/package/image) so it doesn't linger after the drug box empties.
        this.drugDetails = null;
        this.disarmBilledSupersede();
        this.setPrescriptionDefaultValues();

        // Setting imageID will trigger the @Watch decorator which fetches image information
        this.prescription.imageID = imageID;
    }

    clampFillDateToEffective(defaultDate: Date): Date {
        // Delegates to the pure module so the clamping rule has one definition.
        return clampFillDateToEffectivePure({
            defaultDate,
            effectiveDate: this.prescription.effectiveDate,
            enforceEscriptEffectiveDate: this.enforceEscriptEffectiveDate,
        });
    }

    setPrescriptionDefaultValues() {
        //Default values need to be assigned to another variable
        this.prescription.originalRxDate = moment().utc(true).toDate();
        this.prescription.fillDate = this.clampFillDateToEffective(moment().utc(true).toDate());

        this.dawCode = DAWCode.NotSpecified;
        this.originCode = OriginCode.NotSpecified;
        this.statusCode = RefillStatusCode.Refillable;
        this.substatusCode = null;
        this.writtenDate = moment().utc(true).toDate();
    }

    createPrescriptionFromTransfer(transferId?: number) {
        if (!transferId) return;
        Axios.get(`ProgramTransfer/${transferId}`).then((resp) => {
            this.escriptResponseMessage = resp.data;
            // console.log("programTransfer resp: ", resp);
            this.prescription = new Prescription(resp.data.prescription);
            this.prescription.originalRxDate = moment().utc(true).toDate();
            const utcTransferDate = moment.utc(resp.data.prescription.writtenDate);
            this.writtenDate = new Date(utcTransferDate.year(), utcTransferDate.month(), utcTransferDate.date());

            this.prescription.fillDate = this.clampFillDateToEffective(moment().utc(true).toDate());
            this.prescription.dispensed = this.prescription.quantity;
            this.dispensedQuantity = this.prescription.dispensed;
            this.originCode = OriginCode.Transfer;
            this.prescription.originCode = OriginCode.Transfer;

            this.drug = new Drug(this.prescription.packageID);
            this.drug.source = this.prescription.drugSource;
            this.getDrugDetails(this.drug);

            if (resp.data.programObj?.id) {
                this.program = new Program(resp.data.programObj);
                this.programSetFromTransfer = true;
            } else {
                this.program = new Program();
                this.setRecommendedProgram();
            }
            this.store = new Store(resp.data.storeObj);
            this.patient = new Patient(resp.data.patientObj);
            this.doctor = new Prescriber(resp.data.prescriberObj);
            this.supervisingPhysician = this.prescription.supervisingPhysicianId
                ? Object.assign(new Prescriber(), { id: this.prescription.supervisingPhysicianId })
                : null;

        });
    }

    createPrescriptionEscript(escriptID?: number) {
        console.log("Creating prescription from escript");

        if (escriptID) {
            this.originCode = OriginCode.Escript;
            EscriptService.getById(escriptID)
                .then((escriptResponse) => {
                    // Bridge: a follow-up refactors this escript mapper into a
                    // pure module. Until then, re-wrap the service's data so the
                    // existing multi-branch block stays byte-for-byte intact.
                    const resp = { data: escriptResponse };
                    this.escriptResponseMessage = resp.data.message;
                    this.escript = new Escript(resp.data.eScript);

                    if (resp.data.message?.kind === "NewRx") {
                        this.prescription = new Prescription(resp.data.message.prescription);
                        this.prescription.originalRxDate = moment().utc(true).toDate();
                        this.prescription.fillDate = this.clampFillDateToEffective(moment().utc(true).toDate());
                        //Force the written date to the provided day (ignore time/time zone)
                        this.prescription.writtenDate = moment(this.prescription.writtenDate.toString().substring(0, 10)).toDate();
                        this.prescription.escriptID = escriptID;
                        this.prescription.originCode = OriginCode.Escript;
                        this.prescription.daySupply = this.escript.daySupply;
                        this.prescription.directions = resp.data.eScript.smartSig?.sigText ?? resp.data.message?.prescription?.directions;
                        this.prescription.memo = resp.data.eScript.smartSig?.note ?? resp.data.message?.prescription?.memo;
                        this.changeDispense(this.prescription.quantity || 0);
                        this.store = Object.assign(new Store(), resp.data.message.storeObj);

                        this.patient = Object.assign(new Patient(), resp.data.message.patientObj);
                        this.mergeEscriptPatientData(resp.data.message.patient);
                        this.doctor = Object.assign(
                            new Prescriber(),
                            resp.data.message.prescriberObj,
                        );
                        this.supervisingPhysician = resp.data.message.supervisorObj
                            ? Object.assign(new Prescriber(), resp.data.message.supervisorObj)
                            : null;

                        if (resp.data.preferredDrugObj) {
                            if (
                                resp.data.message.drugObj.packageID !=
                                resp.data.preferredDrugObj.packageID
                            ) {
                                this.showDrugChange = true;
                            }
                            this.drug = new Drug(null, resp.data.preferredDrugObj);
                        } else {
                            this.drug = new Drug(null, resp.data.message.drugObj);
                        }

                        this.setViewValues(true);
                    } else if (resp.data.message?.kind === "RxRenewalResponse") {
                        this.prescription = new Prescription(resp.data.message.prescription);
                        this.prescription.originalRxDate = moment().utc(true).toDate();
                        this.prescription.fillDate = this.clampFillDateToEffective(moment().utc(true).toDate());
                        //Force the written date to the provided day (ignore time/time zone)
                        this.prescription.writtenDate = moment(this.prescription.writtenDate.toString().substring(0, 10)).toDate();
                        this.prescription.escriptID = escriptID;
                        this.prescription.originCode = OriginCode.Escript;
                        this.prescription.daySupply = this.escript.daySupply;
                        this.prescription.directions = resp.data.eScript.smartSig?.sigText ?? resp.data.message?.prescription?.directions;
                        this.prescription.memo = resp.data.eScript.smartSig?.note ?? resp.data.message?.prescription?.memo;
                        this.changeDispense(this.prescription.quantity || 0);

                        this.store = Object.assign(new Store(), resp.data.message.storeObj);

                        this.patient = Object.assign(new Patient(), resp.data.message.patientObj);
                        this.mergeEscriptPatientData(resp.data.message.patient);
                        this.doctor = Object.assign(
                            new Prescriber(),
                            resp.data.message.prescriberObj,
                        );
                        this.supervisingPhysician = resp.data.message.supervisorObj
                            ? Object.assign(new Prescriber(), resp.data.message.supervisorObj)
                            : null;

                        if (resp.data.preferredDrugObj) {
                            if (
                                resp.data.message.drugObj.packageID !=
                                resp.data.preferredDrugObj.packageID
                            ) {
                                this.showDrugChange = true;
                            }
                            this.drug = new Drug(null, resp.data.preferredDrugObj);
                        } else {
                            this.drug = new Drug(null, resp.data.message.drugObj);
                        }

                        this.setViewValues(true);
                    } else if (resp.data.message?.kind === "RxChangeResponse") {
                        this.prescription = new Prescription(resp.data.message.prescription);
                        this.prescription.originalRxDate = moment().utc(true).toDate();
                        this.prescription.fillDate = this.clampFillDateToEffective(moment().utc(true).toDate());
                        //Force the written date to the provided day (ignore time/time zone)
                        this.prescription.writtenDate = moment(this.prescription.writtenDate.toString().substring(0, 10)).toDate();
                        this.prescription.escriptID = escriptID;
                        this.prescription.originCode = OriginCode.Escript;
                        this.prescription.daySupply = this.escript.daySupply;
                        this.prescription.directions = resp.data.eScript.smartSig?.sigText ?? resp.data.message?.prescription?.directions;
                        this.prescription.memo = resp.data.eScript.smartSig?.note ?? resp.data.message?.prescription?.memo;
                        this.changeDispense(this.prescription.quantity || 0);
                        this.store = Object.assign(new Store(), resp.data.message.storeObj);
                        this.patient = Object.assign(new Patient(), resp.data.message.patientObj);
                        this.mergeEscriptPatientData(resp.data.message.patient);
                        this.doctor = Object.assign(
                            new Prescriber(),
                            resp.data.message.prescriberObj,
                        );
                        this.supervisingPhysician = resp.data.message.supervisorObj
                            ? Object.assign(new Prescriber(), resp.data.message.supervisorObj)
                            : null;

                        if (resp.data.preferredDrugObj) {
                            if (
                                resp.data.message.drugObj.packageID !=
                                resp.data.preferredDrugObj.packageID
                            ) {
                                this.showDrugChange = true;
                            }
                            this.drug = new Drug(null, resp.data.preferredDrugObj);
                        } else {
                            this.drug = new Drug(null, resp.data.message.drugObj);
                        }

                        this.setViewValues(true);
                    } else if (resp.data.message?.kind === "RxTransfer") {
                        this.prescription = new Prescription(resp.data.message.prescription);
                        this.prescription.originalRxDate = moment().utc(true).toDate();
                        this.prescription.fillDate = this.clampFillDateToEffective(moment().utc(true).toDate());
                        //Force the written date to the provided day (ignore time/time zone)
                        this.prescription.writtenDate = moment(this.prescription.writtenDate.toString().substring(0, 10)).toDate();
                        this.prescription.escriptID = escriptID;
                        this.prescription.originCode = OriginCode.Transfer;
                        this.prescription.daySupply = resp.data.message.drugs.daysSupply;
                        this.prescription.directions = resp.data.eScript.smartSig?.sigText ?? resp.data.message?.prescription?.directions;
                        this.prescription.memo = resp.data.eScript.smartSig?.note ?? resp.data.message?.prescription?.memo;
                        this.changeDispense(this.prescription.quantity || 0);
                        this.store = Object.assign(new Store(), resp.data.message.storeObj);
                        this.originPharmacy = Object.assign(new PharmacyRecord(), resp.data.message.originPharmacyObj);

                        this.patient = Object.assign(new Patient(), resp.data.message.patientObj);
                        this.mergeEscriptPatientData(resp.data.message.patient);
                        this.doctor = Object.assign(
                            new Prescriber(),
                            resp.data.message.prescriberObj,
                        );
                        this.supervisingPhysician = resp.data.message.supervisorObj
                            ? Object.assign(new Prescriber(), resp.data.message.supervisorObj)
                            : null;

                        if (resp.data.preferredDrugObj) {
                            if (
                                resp.data.message.drugObj.packageID !=
                                resp.data.preferredDrugObj.packageID
                            ) {
                                this.showDrugChange = true;
                            }
                            this.drug = new Drug(null, resp.data.preferredDrugObj);
                        } else {
                            this.drug = new Drug(null, resp.data.message.drugObj);
                        }

                        this.setViewValues(true);
                    } else if (resp.data.message?.exception) {
                        const errorMsg = "There was an exception while loading Escript data.";
                        this.$notification(
                            NotificationOptions.notificationOptionsPreset(
                                errorMsg,
                                NotificationOptions.NotificationTypes.danger,
                            ),
                        );
                        console.log(errorMsg, resp.data.message.exception);
                    } else {
                        const errorMsg = "Unknown error, not able to load escript data.";
                        this.$notification(
                            NotificationOptions.notificationOptionsPreset(
                                errorMsg,
                                NotificationOptions.NotificationTypes.danger,
                            ),
                        );
                        console.log(errorMsg, resp);
                    }
                })
                .catch((err) => {
                    const errorMsg = "Error while loading Escript data";
                    this.$notification(
                        NotificationOptions.notificationOptionsPreset(
                            errorMsg,
                            NotificationOptions.NotificationTypes.danger,
                        ),
                    );
                    console.log(errorMsg, {
                        err,
                        response: err.response,
                    });
                });
        }
    }

    /**
     * Merges height and weight from the parsed eScript patient data into the current patient.
     * The parsed patient comes directly from the eScript XML and may have height/weight
     * that the existing DB patient record does not.
     */
    mergeEscriptPatientData(escriptPatient?: Patient) {
        if (!escriptPatient) return;
        if (escriptPatient.height != null) this.patient.height = escriptPatient.height;
        if (escriptPatient.weight != null) this.patient.weight = escriptPatient.weight;
    }

    startEditPatient() {
        this.editPatientForm = true;
    }

    clearForm(continueSameImage = false, clearQueryParams = true) {
        if (this.prescriptionLock && this.prescriptionLock.refreshURL) {
            this.releaseLockAtURL(this.prescriptionLock.refreshURL);
            this.prescriptionLock = null;
        }
        this.clearPrescription(continueSameImage, clearQueryParams);
        this.programSetFromTransfer = false;
        this.drugCategories = [];
        this.drugAlert = "";
        this.claims = [];
        (this.$refs.insuranceComponent as any).clearComponent();
        this.setPrescriptionDefaultValues();

        this.enableExpirationDateEstimation = true;
        this.showDocuStore = false;
        this.initialDispensedQty = 0;

        this.newMode = false;
        this.newRefillMode = false;
        this.disarmBilledSupersede();
        this.$nextTick(() => {
            this.$forceUpdate();
        });
    }

    clearPrescription(continueSameImage = false, clearQueryParams = true) {
        if (this.prescriptionLock && this.prescriptionLock.refreshURL) {
            this.releaseLockAtURL(this.prescriptionLock.refreshURL);
            this.prescriptionLock = null;
        }
        this.prescription = new Prescription();

        this.lastFill = new Prescription();
        this.latestFillStatus = null;

        if (!continueSameImage) {
            this.patient = new Patient();
            this.doctor = new Prescriber();
            this.supervisingPhysician = null;
            this.store = new Store();
            this.escript = new Escript();
            this.savedEscriptID = undefined;
            this.cancelRxEscriptID = null;
            this.savedProgramTransfer = undefined;
            this.savedIngestionQueueItemID = null;
            this.queueItem = null;
        }

        this.program = new Program();
        this.drug = new Drug();
        this.expiration = null;
        this.writtenDate = null;
        this.drugDetails = null;
        this.dispenseError = new DispenseError();
        this.dispenseErrors = [];

        this.dispensedQuantity = 0;

        if (clearQueryParams) {
            // when we clear, we need to clear the imageID query if it exists.
            this.clearQuery();
        }
    }

    undoChanges() {
        this.clearForm();
        window.scrollTo(0, 0);
    }

    // Thin wrapper over the pure hydrateViewValues. The two side effects a pure
    // function cannot own stay here: normalizing prescription.fillDate to a Date,
    // and toggling the settingViewValues guard.
    setViewValues(_isEscript = false) {
        this.settingViewValues = true;
        this.prescription.fillDate = moment(this.prescription.fillDate).toDate();

        const view = hydrateViewValues(this.prescription);
        this.dawCode = view.dawCode;
        this.originCode = view.originCode;
        this.statusCode = view.statusCode;
        this.substatusCode = view.substatusCode;
        this.nextTouchDateLocal = view.nextTouchDateLocal;
        this.nextTouchDateManualOverride = view.nextTouchDateManualOverride;
        this.deliveryCode = view.deliveryCode;
        this.dispensedQuantity = view.dispensedQuantity;
        this.expiration = view.expiration;
        this.writtenDate = view.writtenDate;
        this.treatmentTypeCode = view.treatmentTypeCode;

        this.settingViewValues = false;
    }

    // Thin wrapper over the pure applyViewValues. dispensedQuantity is passed in
    // the view but deliberately NOT written back (the mirror is asymmetric);
    // originPharmacyId is copied only when the origin pharmacy has loaded.
    assignViewValues() {
        Object.assign(
            this.prescription,
            applyViewValues(
                {
                    dawCode: this.dawCode,
                    originCode: this.originCode,
                    statusCode: this.statusCode,
                    substatusCode: this.substatusCode,
                    nextTouchDateLocal: this.nextTouchDateLocal,
                    nextTouchDateManualOverride: this.nextTouchDateManualOverride,
                    deliveryCode: this.deliveryCode,
                    dispensedQuantity: this.dispensedQuantity,
                    expiration: this.expiration,
                    writtenDate: this.writtenDate,
                    treatmentTypeCode: this.treatmentTypeCode,
                },
                { originPharmacyId: this.originPharmacy?.id },
            ),
        );
    }

    @Watch("statusCode") statusCodeUpdate(newValue: number, oldValue: number) {
        // Guarded on the same getter that disables the Fill Date picker: once a claim has paid, the
        // date is both the billed date of service and uneditable, so rewriting it here would strand
        // the user with a date they cannot correct.
        if (
            (oldValue === RefillStatusCode.OnHold ||
                oldValue === RefillStatusCode.AwaitingInsurance) &&
            newValue !== RefillStatusCode.OnHold &&
            newValue !== RefillStatusCode.AwaitingInsurance &&
            !this.rxWasBilled
        ) {
            this.prescription.fillDate = this.clampFillDateToEffective(moment().utc(true).toDate());
        }

        // Clear NextTouchDate when leaving on-hold status
        if (oldValue === RefillStatusCode.OnHold && newValue !== RefillStatusCode.OnHold) {
            this.nextTouchDateLocal = "";
            this.nextTouchDateManualOverride = false;
        }
    }

    @Watch("substatusCode") substatusCodeUpdate() {
        if (this.settingViewValues) return;
        if (this.statusCode !== RefillStatusCode.OnHold || this.nextTouchDateManualOverride) return;

        const selected = this.onHoldSubStatuses.find(ss => ss.value === this.substatusCode);
        if (selected?.offsetValue && selected.offsetValue > 0 && selected.offsetUnit) {
            const unit = selected.offsetUnit.toLowerCase() as moment.unitOfTime.DurationConstructor;
            this.nextTouchDateLocal = moment().add(selected.offsetValue, unit).format("YYYY-MM-DDTHH:mm");
        } else {
            this.nextTouchDateLocal = "";
        }
    }

    // The image panel owns the annotator; the top-row Edit button and the Hardcopy Note button
    // drive it through the typed @Ref. These delegate to the panel's own methods — they are
    // separate components, so identically named methods on each never collide.
    openPdfAnnotator() {
        this.imagePanel?.openAnnotator();
    }

    openQuickAnnotate() {
        this.imagePanel?.openQuickAnnotate(this.addNote);
    }

    openAddImage() {
        this.imagePanel?.openAddImage();
    }

    // The panel emits rather than mutating the prescription; the container owns the imageID.
    onImageUpdated(payload: { imageId: number }) {
        if (!this.prescription) return;
        this.prescription.imageID = payload.imageId;
    }

    // The panel emits when a quick-annotate save consumes the note; clear it unless "keep note".
    onImageNoteConsumed() {
        if (!this.keepHardcopyNote) {
            this.addNote = "";
        }
    }

    // AnnotationRevertMixin refresh seam: our ImageComponent lives inside RxImagePanel.
    protected refreshRevertedImage(imageId: number | null) {
        this.imagePanel?.refreshImage(imageId ?? undefined);
    }

    saveAndContinue() {
        this.runEquivalentsGateOnce = false;
        this.orchestrator.requestSave(createSaveIntent());
    }

    saveAndBill(billInsuranceCallback: Function) {
        if (this.validFormForBilling) {
            this.runEquivalentsGateOnce = false;
            this.orchestrator.requestSave(createSaveIntent({ billPlanCallback: billInsuranceCallback as SaveIntent["billPlanCallback"] }));
        } else {
            billInsuranceCallback(null, true);
            const message =
                "Not possible to save and bill. Please, first fill all required fields." +
                (this.fieldsPending ? " Check the next fields: " + this.fieldsPending : "");
            this.$bvModal.msgBoxOk(message, {
                title: "Not possible to save and bill.",
                size: "sm",
                buttonSize: "sm",
                okVariant: "danger",
                footerClass: "p-2",
                hideHeaderClose: false,
                centered: true,
            });
        }
    }

    saveAndStay() {
        this.saveAndFinish(createSaveIntent({ finish: true, stayOnPage: true }));
    }

    saveAndFinish(intent: SaveIntent) {
        if (this.dispenseError?.id) {
            // Stash the intent so the resolve-confirmation modal can resume the save with the
            // original stay-on-page / finish choice rather than re-deriving it.
            this.dispenseResolveIntent = intent;
            this.dispenseErrorResolveModal.show();
        } else {
            if (this.validFormForBilling) {
                // save()/saveAndStay() are the equivalents-gate entry points; the orchestrator
                // runs that gate (as an awaiting-state) off this flag.
                this.runEquivalentsGateOnce = true;
                this.orchestrator.requestSave(intent);
            } else {
                const message =
                    "Not possible to save. Please, first fill all required fields." +
                    (this.fieldsPending ? " Check the next fields: " + this.fieldsPending : "");
                this.$bvModal.msgBoxOk(message, {
                    title: "Not possible to save.",
                    size: "sm",
                    buttonSize: "sm",
                    okVariant: "danger",
                    footerClass: "p-2",
                    hideHeaderClose: false,
                    centered: true,
                });
            }
        }
    }

    onEquivalentDiscontinued(result: any) {
        this.equivalentPrescriptionsData = [];
        this.orchestrator.resumeEquivalentsHandled();
    }

    onEquivalentSkipped() {
        this.equivalentPrescriptionsData = [];
        this.orchestrator.resumeEquivalentsHandled();
    }

    onEquivalentDismissed() {
        // Closed via X / Esc without discontinuing or skipping: drop the attempt parked at
        // the equivalents gate so a later discontinued/skipped emission can't resume a stale save.
        this.equivalentPrescriptionsData = [];
        this.orchestrator.cancelPending('equivalents-prompt');
    }

    // DispenseErrorResolveModal "@resolved": the operator confirmed the error was resolved. Carry
    // resolveDispenseErrorId on the save intent so the orchestrator marks the error resolved ONLY on
    // the post-save success path (buildSavePorts.resolveDispenseError) — a failed save then leaves
    // the error open instead of resolved against an Rx that was never saved. The modal no longer
    // POSTs the resolve up front. Resume the intent stashed when saveAndFinish diverted here, so
    // stay-on-page survives the confirmation modal. This path bypasses the equivalents gate (per the
    // entry-point matrix).
    onDispenseErrorResolved(payload: { resolvedErrorId: number }) {
        const stashed = this.dispenseResolveIntent;
        this.dispenseResolveIntent = null;
        this.runEquivalentsGateOnce = false;
        this.orchestrator.requestSave(createSaveIntent({
            finish: true,
            stayOnPage: stashed?.stayOnPage ?? false,
            resolveDispenseErrorId: payload.resolvedErrorId,
        }));
    }

    // Adapter for the save orchestrator (buildSavePorts.refreshDispenseErrors): re-fetch the
    // dispense-error alert list so it reflects the true state after a (post-save) resolve.
    refreshDispenseErrors() {
        this.fetchDispenseError();
    }

    // DispenseErrorResolveModal "@proceed": save without resolving (checkbox left unchecked).
    // Resume the intent stashed by saveAndFinish wholesale — it already carries finish / stayOnPage /
    // goNextRx / billPlanCallback and needs no resolve id, so the finish-gated navigation still fires.
    onDispenseErrorProceed() {
        const stashed = this.dispenseResolveIntent;
        this.dispenseResolveIntent = null;
        this.runEquivalentsGateOnce = false;
        this.orchestrator.requestSave(stashed ?? createSaveIntent());
    }

    onUseGenericFromPrompt(generic: Drug.EquivalentDrug) {
        if (!generic.packageID) {
            this.$notification(NotificationOptions.error("Selected generic is missing a package id."));
            return;
        }

        // Don't silently swap the dispensed drug on a fill that's already committed — verified
        // (PV2), shipped, or carrying an active (non-reversed) billing claim. Reassigning the
        // package and re-saving here would change the dispensed/billed drug out from under that
        // record. On a committed fill the only safe in-prompt choices are Keep Brand (logs the
        // DAW decision) or Cancel; switching the drug must go through the normal reverse/void flow.
        const isCommittedFill = !this.newMode && !this.newRefillMode &&
            (this.prescription.hasBeenShipped || this.prescription.hasBeenVerified || this.hasActiveClaims);
        if (isCommittedFill) {
            this.$notification(NotificationOptions.error(
                "This fill has already been verified, billed, or shipped — change the drug manually after reversing or voiding as needed."));
            return;
        }

        // The orchestrator applies the drug swap (buildSavePorts.applyGenericSwap) and then
        // continues the deferred save.
        this.orchestrator.resumeUseGeneric(generic);
    }

    onKeepBrandFromPrompt(payload: KeepBrandPayload) {
        // The orchestrator applies the chosen DAW (buildSavePorts.applyKeepBrandDaw), continues
        // the deferred save, and posts the brand-kept audit row after the save succeeds.
        this.orchestrator.resumeKeepBrand(payload);
    }

    onBrandToGenericDismissed() {
        // A dismissed prompt (no use-generic / keep-brand) must not leave the save parked.
        this.orchestrator.cancelPending('generic-prompt');
    }

    popQueue() {
        if (!(this.$store.queueItems?.length ?? 0)) return false;

        const nextItem = this.$store.queueItems?.pop();

        // Build query object, only including properties with actual values
        const query: Record<string, string> = {};
        if (nextItem?.imageID) query.imageID = String(nextItem.imageID);
        if (nextItem?.escriptID) query.escriptID = String(nextItem.escriptID);
        if (nextItem?.id) query.queueItemID = String(nextItem.id);
        if (nextItem?.programTransferId) query.programTransfer = String(nextItem.programTransferId);

        this.$router.replace({
            name: "Prescription",
            query,
        });

        return true;
    }

    // The post-save continuation (SavePorts.nav.completeSave): runs after the orchestrator's
    // save POST + applySaved. The decision — which ingestion/navigation/form-reset/emit steps
    // to run, in what order — is the pure decidePostSaveContinuation; this method builds its
    // context, then executes the returned actions plus the on-page bill/next-Rx tail.
    async runPostSaveContinuation(intent: SaveIntent, _saved: SavedPrescription): Promise<void> {
        this.recordRxFocusTime(this.prescription.rxID, PageFocusEventClosedAction.Completed);
        // Only a save that keeps this Rx on screen reopens a segment; a finishing save navigates away.
        this.rxFocusReopenedAfterSave = intent.stayOnPage || !!intent.billPlanCallback;
        if (this.rxFocusReopenedAfterSave) {
            this.rxFocusLoadedAt = performance.now();
            this.rxFocusVisibleSeconds = 0;
        }

        // Capture the queue item id before any route change can blank queueItemID; it falls back
        // to savedIngestionQueueItemID, which persists across repeated saves of the same image.
        const savedQueueItemID = this.queueItemID || this.savedIngestionQueueItemID;
        const actions = decidePostSaveContinuation(
            this.buildPostSaveContext(intent, savedQueueItemID),
        );

        // A finishing (non-batch, non-stay) save that matches no continuation case — no completable
        // queue item and no routed Rx id — yields no actions. Preserve the legacy breadcrumb so
        // support isn't left with a silent no-op.
        if (intent.finish && !intent.goNextRx && !intent.stayOnPage && actions.length === 0) {
            console.error("Something went wrong while identifying the case of saving", {
                rxId: this.prescription.rxID,
                fromIngestion: this.fromIngestion,
                queueItemID: savedQueueItemID,
                routeRxId: this.routeRxId,
                finish: intent.finish,
            });
        }

        // Capture new-mode state BEFORE the fire-and-forget actions run: a Save-and-New reset
        // (resetFormForNewRxSameImage) forces newMode true synchronously, and the bill tail below
        // needs the pre-reset value.
        const wasNewRx = this.newMode || this.newRefillMode;

        // Awaited before any action: Save and New resets the form synchronously and a finishing save
        // navigates away, and both release only a lock that is already registered.
        await this.registerSavedPrescriptionLock();

        // Fire-and-forget, exactly like the legacy nested promise: the bill / next-Rx tail below
        // keeps running in the same relative order (before the async navigation resolves). Errors
        // are swallowed inside executePostSaveActions so a post-save nav hiccup can't be reported
        // as a save failure — the save already succeeded.
        void this.executePostSaveActions(actions);

        // Save-and-Next: advance to the next Rx in the batch (navigation stays on the page).
        if (intent.goNextRx) {
            this.nextRx();
            return;
        }

        if (intent.finish || intent.billPlanCallback) {
            this.newMode = false;
            this.newRefillMode = false;
        }

        if (intent.billPlanCallback) {
            // The save itself is what stamps an automatic hold, so the status we just applied is the
            // first sight of one. Billing here would cash out the script we watched get held: a held
            // save is not an exception, so the orchestrator's failure path never fires for it.
            if (!this.prescription.isBillable) {
                this.$notification(NotificationOptions.warning(
                    "This prescription was placed on hold while saving and has not been billed. "
                    + "Resolve the hold, then cash out.",
                    "Cash Out Skipped"));
                return;
            }

            // Billing a new Rx/refill: once the claim lands, the next save becomes a new-Rx insert
            // so it can't overwrite this billed Rx.
            // Runs after applySaved, so this.prescription.rxID is the final billed id to key the arm on.
            this.billedNewRxAwaitingClaims = wasNewRx;
            this.billedNewRxAwaitingClaimsRxId = wasNewRx ? this.prescription.rxID : null;
            intent.billPlanCallback(this.prescription);
        }
    }

    // Pure-data context for decidePostSaveContinuation. Everything the decision needs, captured
    // before the first action can mutate route/queue state.
    private buildPostSaveContext(intent: SaveIntent, savedQueueItemID: string | null): PostSaveContext {
        const rx = this.prescription;
        const canCompleteIngestion = !!(
            savedQueueItemID && rx && rx.storeID && rx.rxNumber && (rx.rfNumber || 0) >= 0
        );
        const route = this.$route;
        return {
            finish: intent.finish,
            goNextRx: intent.goNextRx,
            stayOnPage: intent.stayOnPage,
            hasBillPlanCallback: !!intent.billPlanCallback,
            willAutorefill: this.willAutorefill,
            isBatchMode: this.isBatchMode,
            fromIngestion: this.fromIngestion,
            canCompleteIngestion,
            savedQueueItemId: savedQueueItemID,
            hasRouteRxId: !!this.routeRxId,
            autoModeEnabled: !!this.$store.autoModeEnabled,
            routeHasQueueItemIdQuery: !!route.query.queueItemID,
            routeIsPrescriptionDetails: route.name === "PrescriptionDetails",
        };
    }

    // Execute the ordered post-save actions. Runtime results the pure decision can't know —
    // whether auto-mode/queue already advanced to the next item — gate the back-navigation here;
    // the back destination is resolved live (backToPreviousPage) after any route re-point.
    private async executePostSaveActions(actions: PostSaveAction[]): Promise<void> {
        let navigatedToNextItem = false;
        let poppedQueueItem = false;
        try {
            for (const action of actions) {
                switch (action.kind) {
                    case "replaceRouteToSavedRx":
                        this.$router.replace(this.savedRxRoute());
                        break;
                    case "pushRouteToSavedRx":
                        this.$router.push(this.savedRxRoute());
                        break;
                    case "completeIngestion": {
                        const result = await this.completeIngestionForSavedRx(
                            action.queueItemId,
                            action.advanceAutoMode,
                        );
                        // A finishing save aborts the whole continuation when the mark fails
                        // (legacy reject); Save-and-New continues so the form still clears.
                        if (result.failed && action.abortOnFailure) return;
                        navigatedToNextItem = result.navigated;
                        break;
                    }
                    case "advanceAutoMode":
                        navigatedToNextItem = await this.handleAutoModeCompletion();
                        break;
                    case "popQueue":
                        poppedQueueItem = this.popQueue();
                        break;
                    case "backToPrevious":
                        // Only navigate back if neither auto mode nor the legacy queue advanced.
                        // backToPreviousPage re-derives the destination from live state, so a prior
                        // route re-point that flipped fromIngestion is honored (as it was legacy).
                        if (!navigatedToNextItem && !poppedQueueItem) {
                            if (action.clearQuery) this.clearQuery();
                            this.backToPreviousPage(action.skipBillingValidation);
                        }
                        break;
                    case "resetFormForNewRx": {
                        this.resetFormForNewRxSameImage();
                        // Three outcomes: the hardcopy carried forward, an eScript stayed behind with the Rx just
                        // saved, or there was never a document to carry. Keyed on savedEscriptID rather than the
                        // absence of an image, so a manual Rx is not told about an eScript it never had. With no
                        // image to stamp, a retained note has nowhere to go, so it is only promised when one carried.
                        const noteRetained = this.keepHardcopyNote && this.addNote ? " Hardcopy note has been retained." : "";
                        let message: string;
                        if (this.prescription.imageID) {
                            message = `You can start introducing information for the new RX with the same image.${noteRetained}`;
                        } else if (this.savedEscriptID) {
                            message = "You can start introducing information for the new RX. The eScript stays with the prescription just saved, so this one will have no hardcopy attached.";
                        } else {
                            message = "You can start introducing information for the new RX.";
                        }
                        this.$notification(
                            NotificationOptions.notificationOptionsPreset(
                                message,
                                NotificationOptions.NotificationTypes.info,
                            ),
                        );
                        break;
                    }
                    case "emitBatchFinished":
                        this.$emit("batchFinished", this.prescription);
                        break;
                }
            }
        } catch (error) {
            // The save already succeeded; a continuation failure must not surface as a save error.
            console.error("Post-save continuation error:", error);
        }
    }

    // The saved-Rx route the ingestion flow re-points to (replace or push).
    private savedRxRoute() {
        return {
            name: "PrescriptionDetails",
            params: {
                storeID: this.prescription.storeID.toString(),
                rxNumber: this.prescription.rxNumber.toString(),
                rfNumber: this.prescription.rfNumber?.toString() ?? "",
            },
            query: { preserveScroll: "1" },
        };
    }

    // Mark the saved ingestion queue item complete, then (when advanceAutoMode) auto-mode advance.
    // Returns whether auto-mode navigated, and whether the mark failed.
    private async completeIngestionForSavedRx(
        queueItemId: string,
        advanceAutoMode: boolean,
    ): Promise<{ navigated: boolean; failed: boolean }> {
        const rx = this.prescription;
        const queueItem: QueueItem = {
            id: +queueItemId,
            storeID: +rx.storeID,
            rxNumber: +rx.rxNumber,
            rfNumber: +(rx.rfNumber || 0),
        } as QueueItem;

        try {
            const completed = await IngestionQueueService.complete([queueItem]);
            console.log(`IngestionQueue marking complete:`, completed);
            const navigated = advanceAutoMode ? await this.handleAutoModeCompletion() : false;
            return { navigated, failed: false };
        } catch (error) {
            console.error(`IngestionQueue marking complete error:`, {
                error,
                response: (error as any)?.response,
            });
            this.$notification(NotificationOptions.error(error));
            return { navigated: false, failed: true };
        }
    }

    // Convert the just-billed Rx into a new-Rx draft in place: keep the entered clinical values and image/patient/prescriber, but clear the billed identity so the next save inserts a new Rx instead of overwriting it.
    // Returns a restore that puts the form back exactly as it was (invoked when the supersede POST
    // fails). Snapshot and mutation live together: any field this method (or clearBilledFillIdentity)
    // touches must be in the snapshot. A shallow field snapshot suffices — fields/arrays are
    // replaced, not mutated — and restoring onto the same prescription instance keeps its
    // prototype getters and Vue observers intact (a spread also can't trip getter-only props).
    prepareNewRxFromBilled(): () => void {
        const savedRxFields = { ...this.prescription };
        const saved = {
            claims: this.claims,
            // Copied, not aliased: the dispense-item editor mutates this array in place.
            localDispenseItems: [...this.localDispenseItems],
            statusCode: this.statusCode,
            substatusCode: this.substatusCode,
            newMode: this.newMode,
            newRefillMode: this.newRefillMode,
        };
        this.newMode = true;
        this.newRefillMode = false;
        this.prescription.rxNumber = undefined as unknown as number;
        this.prescription.rfNumber = undefined;
        this.prescription.pV2ID = null;
        this.prescription.orders = [];
        this.prescription.claims = [];
        this.claims = [];
        // Clear the escript link so the server can't re-adopt the billed Rx number and overwrite it; imageID is kept so the hardcopy stays visible.
        this.prescription.escriptID = undefined;
        this.prescription.aspnRxID = null;
        this.prescription.partnerFillNumber = "";
        this.prescription.originPharmacyId = undefined;
        this.prescription.transferMyRxId = undefined;
        this.prescription.programTransferId = undefined;
        this.prescription.status = RefillStatusCode.Refillable;
        this.prescription.subStatus = null;
        this.statusCode = RefillStatusCode.Refillable;
        this.substatusCode = null;
        // Reset dispense totals so the body matches a genuinely-new Rx — the billed Rx's totals would otherwise trip the controller's "maximum remaining" guard.
        this.prescription.totalDispensed = undefined as unknown as number;
        this.prescription.totalAllowedToDispense = undefined as unknown as number;
        this.clearBilledFillIdentity();
        this.prescription.fillDate = this.clampFillDateToEffective(moment().utc(true).toDate());
        return () => {
            Object.assign(this.prescription, savedRxFields);
            Object.assign(this, saved);
        };
    }

    clearBilledFillIdentity() {
        this.prescription.priceScheduleID = undefined;
        this.prescription.cost = null;
        this.prescription.patientPay = null;
        this.prescription.totalPrice = null;
        this.prescription.tax = null;
        this.prescription.lotNumber = "";
        this.prescription.serialNumber = "";
        this.prescription.drugsFilledDate = null;
        this.prescription.drugExpirationDate = null;
        this.localDispenseItems = [];
    }

    // No mount() here: a remount re-runs the async createPrescription* path and clobbers the restored ids.
    resetFormForNewRxSameImage() {
        const preservedOriginCode = this.originCode;
        const preservedImageID = this.prescription.imageID;
        const preservedEscriptID = this.prescription.escriptID;
        const preservedAddNote = this.keepHardcopyNote ? this.addNote : "";
        this.clearForm(true);
        this.newMode = true;
        this.prescription.storeID = Number(this.store.id);
        this.prescription.patientID = Number(this.patient.id);
        this.prescription.prescriberID = Number(this.doctor.id);
        this.originCode = preservedOriginCode;
        this.addNote = preservedAddNote;
        if (this.queueItem?.imageID) {
            this.prescription.imageID = this.queueItem.imageID;
        } else if (preservedImageID) {
            this.prescription.imageID = preservedImageID;
        }
        if (preservedEscriptID) {
            this.prescription.escriptID = preservedEscriptID;
            // The link stays so the new Rx records the document it was typed from; this tells the server to
            // insert rather than resolve the payload to the eScript's existing Rx and overwrite it.
            this.prescription.createNewFromEscript = true;
        }
    }

    getErrorValidation() {
        if (this.prescription.daySupply === 0) {
            return true;
        }

        if (this.validationClasses.daySupply === "has-danger") {
            this.$nextTick(() => {
                this.$notification(NotificationOptions.error("Enter valid value for days supply"));
                this.saving = false;
            });
            return false;
        }

        if (this.validationClasses.refills === "has-danger") {
            this.$nextTick(() => {
                this.$notification(NotificationOptions.error("Enter valid value for refills"));
                this.saving = false;
            });
            return false;
        }

        return true;
    }

    clearQuery() {
        // when we clear, we need to clear the imageID query if it exists.
        if (Object.keys(this.$route.query).length) {
            console.log("clearing query");
            this.$router.replace({
                name: this.$route.name,
                query: {},
            } as Location);
        }
    }

    onPatientCreated(newPatient: Patient) {
        this.editPatientForm = false;
        this.showPatientForm = false;
        this.patient = newPatient;
        this.$bvModal.hide("create-patient");
    }

    onDoctorSaved(newDoctor: Prescriber) {
        this.doctorFormMode = null;
        this.doctor = newDoctor;
        this.$bvModal.hide("create-doctor");
    }

    onDoctorFormClose() {
        const wasCreate = this.doctorFormMode === "create";
        this.doctorFormMode = null;
        if (wasCreate) {
            this.doctor = new Prescriber();
        }
    }

    onSupervisorSaved(prescriber: Prescriber) {
        const wasCreate = this.supervisorFormMode === "create";
        this.supervisorFormMode = null;
        this.supervisingPhysician = prescriber;
        if (wasCreate && this.prescription) {
            this.prescription.supervisingPhysicianId = prescriber.id ?? null;
        }
    }

    onSupervisorFormClose() {
        this.supervisorFormMode = null;
    }

    supervisingPhysicianUpdate(value: Prescriber | null) {
        if (!this.prescription) return;
        this.prescription.supervisingPhysicianId = value?.id ?? null;
    }

    showPrescriptionForm(close?: string) {
        switch (close) {
            case "hidePatientForm":
                this.editPatientForm = false;
                this.showPatientForm = false;
                this.patient = new Patient();
                break;
        }
        this.showDocuStore = false;
    }

    replaceRouterParamsWithCurrentPrescription() {
        if (this.routeRxId == null) return;

        if (!isNaN(Number(this.$route.params.storeID))) {
            this.$router.replace({
                name: "PrescriptionDetails",
                params: {
                    rfNumber: this.prescription.rfNumber?.toString() ?? "",
                    rxNumber: this.prescription.rxNumber?.toString() ?? "",
                    storeID: this.prescription.storeID.toString(),
                },
                query: { preserveScroll: "1" },
            });
            window.location.reload();
            return;
        }
        if (this.$route.params.rxId) {
            this.$router.replace({
                name: "PrescriptionDetailsId",
                params: { rxId: this.prescription.rxID },
                query: { preserveScroll: "1" },
            });
            window.location.reload();
            return;
        }
    }

    // RxTransferActions performs the internal-transfer POST and shows the toast; the container owns
    // the entity replacement and route update (the child never mutates the prescription prop).
    onInternalTransferred(transferredData: any) {
        this.prescription = Object.assign(new Prescription(), transferredData);
        this.replaceRouterParamsWithCurrentPrescription();
    }

    // A 418 from the child's TransferMyRx cancel surfaces here so the page's LockHandler owns the UX.
    onTransferLocked(lockData: unknown) {
        this.showLockedMessage(
            EntityType.Prescription,
            this.prescription?.id?.toString(),
            this.prescription?.toString(),
            lockData,
        );
    }

    equivalentDrugsSearched(drugs: any) {
        this.equivalentDrugs = drugs;
    }

    save() {
        if (this.isBatchMode) {
            this.saveAndNext();
        } else {
            this.saveAndFinish(createSaveIntent({ finish: true }));
        }
    }

    cancel() {
        if (this.isBatchMode) {
            this.nextRx();
        } else {
            this.backToPreviousPage();
        }
    }

    //**BATCH MODE FUNCTIONS */
    saveAndNext() {
        this.runEquivalentsGateOnce = false;
        this.orchestrator.requestSave(createSaveIntent({ finish: true, goNextRx: true }));
    }

    nextRx() {
        const thisRx = this.prescription;
        if (this.batchIndex >= 0) {
            this.clearForm();
            // On the last item there is no next Rx, so this announcement would be false.
            if (!this.isBatchOver && !this.suppressBatchNotifications) {
                this.$notification(
                    NotificationOptions.notificationOptionsPreset(
                        `Next Rx in batch...`,
                        NotificationOptions.NotificationTypes.info,
                    ),
                );
            }
        }

        if (!this.isBatchOver) {
            this.batchIndex++;
            const nextRxId = Object.assign(
                new Prescription(),
                this.prescriptionsInBatch[this.batchIndex],
            ).rxID;
            this.getPrescription(nextRxId, this.refillBatchMode);
            this.scrollToBeginning();

            if (this.isBatchOver) {
                this.buttonTitles.save = "Save and Finish";
                this.buttonTitles.cancel = "";
            }
        } else {
            if (!this.suppressBatchNotifications) {
                this.$notification(
                    NotificationOptions.notificationOptionsPreset(
                        `Batch was finished.`,
                        NotificationOptions.NotificationTypes.info,
                    ),
                );
            }
            this.$emit("batchFinished", thisRx);
        }
    }

    scrollToBeginning() {
        const switchId = "rx-modal___BV_modal_header_";
        const switch_dom = document.getElementById(switchId);
        if (switch_dom) switch_dom.scrollIntoView({ block: "start" });
    }

    //**CUSTOM QUESTIONS METHODS*/
    fetchDrugCategory(productID: number | string = 0) {
        if (!(this.drug?.drugId || productID)) return;

        productID = productID || this.drug?.drugId;
        DrugService.getCategories(productID, this.drug.source)
            .then((categories) => {
                this.drugCategories = categories.map((r) =>
                    Object.assign(new DrugCategory(), r),
                );
            })
            .catch((error) => {
                console.error("Error while getting drug categories", {
                    error,
                    response: error?.response,
                });
            });
    }

    handleSplitComplete(result: { newQueueItems: QueueItem[]; originalScriptUsed: boolean; newImageId: number | null }) {
        console.log("Split complete:", result);

        if (result.originalScriptUsed && result.newImageId) {
            console.log("Original Script used, refreshing with new image:", result.newImageId);
            this.prescription.imageID = result.newImageId;
            // If we have a queue item, update it too
            if (this.queueItem) {
                this.queueItem.imageID = result.newImageId;
            }
            this.$notification(NotificationOptions.success("Document split successfully. The selected pages have been kept with this prescription.", "Split Complete"));
            return;
        }

        const queueItems = result.newQueueItems;
        if (!queueItems || queueItems.length === 0) {
            this.backToPreviousPage(true);
            return;
        }

        if (this.$store.queueItems?.length ?? 0 > 0) {
            this.$store.queueItems?.push(...queueItems);
        } else {
            this.$store.queueItems = queueItems;
        }
        this.popQueue();
    }

    redrawEscript() {
        const escriptId = this.escriptID || this.savedEscriptID;
        if (!escriptId) {
            console.error('No escriptID available for redraw');
            return;
        }
        EscriptService.redraw(escriptId)
            .then(() => {
                window.location.reload();
            })
            .catch((err) => {
                this.$notification(NotificationOptions.error(err));
            });
    }

    // Resolve the QuestionaryComponent rendered for a drug category. Vue 2 exposes a `:ref` placed
    // inside a v-for as an array, so unwrap the single entry.
    private questionaryComponent(catId: number | string): QuestionaryComponent | undefined {
        return (this.$refs[`questionary_${catId}`] as Array<any>)?.[0] as QuestionaryComponent | undefined;
    }

    // Gather the questionaries whose answers the operator actually changed so saveAfterCheck can send
    // them with the Rx POST. Returns an empty array when nothing changed, so an Rx save that doesn't
    // touch any drug-category question makes no answer writes (and shows no answer banner) at all.
    collectChangedQuestionaries(): Questionary[] {
        return (this.drugCategories ?? []).flatMap((cat) => {
            const changed = this.questionaryComponent(cat.id)?.getChangedQuestionary();
            return changed ? [changed] : [];
        });
    }

    // After a successful save, re-baseline each submitted category that actually persisted so a later
    // save on the same page (billing / stay-on-page) doesn't re-send unchanged answers. Categories whose
    // answers failed to save — including a permission drop, where the server returns every submitted
    // category as failed — are left dirty so they retry.
    rebaselineSavedQuestionaries(submitted: Questionary[], failedCategoryIDs: number[]): void {
        const failed = new Set<number>(failedCategoryIDs ?? []);
        submitted.forEach((q) => {
            const catId = q.drugCategoryID;
            if (catId == null || failed.has(catId)) return; // keep failed/unknown categories dirty for retry
            this.questionaryComponent(catId)?.markSaved();
        });
    }

    //Patient Notes
    async refreshPatientNotes(_newNote: Note) {
        if (this.$refs.patientNotesList instanceof NotesList) {
            await (this.$refs.patientNotesList as NotesList).loadNotes();
        }
    }

    openRxHistory() {
        const modal = this.$refs["patient_rx_history_modal"] as PatientRxHistoryModalComponent;
        if (modal) {
            modal.openRxHistory();
        } else {
            console.error("Reference dont exist for the component", modal);
        }
    }

    @Watch("prescription")
    prescriptionUpdated(rx: Prescription) {
        if (
            rx.programName?.includes("COVID") &&
            rx.updatedBy == "PatientWeb" &&
            this.notAllowNewRefill &&
            this.refillBatchMode
        ) {
            console.log(
                "prescription's programName includes 'COVID' and was updated by PatientWeb. Lets skip it.",
            );
            this.nextRx();
        }
    }

    @Watch("expiration")
    expirationDateUpdate(val: Date, _oldVal: Date) {
        // noinspection PointlessBooleanExpressionJS,SuspiciousTypeOfGuard
        if (!(val instanceof Date) || isNaN(val as any)) return;
        this.validationClasses.expirationDate = val ? "has-success" : "has-danger";
    }

    @Watch("prescription.fillDate")
    @Watch("prescription.effectiveDate")
    @Watch("expiration")
    @Watch("writtenDate")
    fillDateUpdate() {
        const fillDate = this.prescription.fillDate;
        // Don't apply validation styling until both dates are available
        if (!fillDate || !this.expiration) {
            this.validationClasses.fillDate = "";
            return;
        }
        const beforeExpiration = moment(fillDate).isSameOrBefore(this.expiration);
        const notBeforeWritten = !this.writtenDate || moment(fillDate).isSameOrAfter(moment(this.writtenDate), "day");
        const notBeforeEffective = !this.enforceEscriptEffectiveDate ||
            !this.prescription.effectiveDate || this.hasSuperAdminPermission ||
            moment(fillDate).isSameOrAfter(moment(this.prescription.effectiveDate), "day");
        const valid = beforeExpiration && notBeforeWritten && notBeforeEffective;
        this.validationClasses.fillDate = valid ? "has-success" : "has-danger";
    }

    @Watch("prescription.daySupply")
    daySupplyUpdate(val: number, _oldVal: number) {
        this.validationClasses.daySupply = val && Number.isInteger(val) ? "has-success" : "has-danger";
    }

    @Watch("prescription.refills")
    refillsUpdate(val: number, _oldVal: number) {
        this.validationClasses.refills = val != null && Number.isInteger(val) ? "has-success" : "has-danger";
    }

    @Watch("dispensedQuantity")
    dispenseUpdate(val: number) {
        if (val == 0) {
            this.validationClasses.dispense = "has-warning";
            return;
        }
        this.validationClasses.dispense = val ? "has-success" : "has-danger";
    }

    @Watch("prescription.quantity")
    quantityUpdate(val: number) {
        if (val == 0) {
            this.validationClasses.quantity = "has-warning";
            return;
        }
        this.validationClasses.quantity = val ? "has-success" : "has-danger";
    }

    // Transition watcher for the two validationClasses keys the (now pure) billing
    // validation owns: dispense and writtenDate. It MERGES into the existing
    // object — never replaces it — because six field watchers own the other keys
    // and getErrorValidation gates save on them. dispense is written only when
    // non-empty to preserve the getter's sticky (never-cleared) behavior;
    // writtenDate is else-cleared, matching the original getter. fieldsPending is
    // NOT latched here — it is a synchronous computed (see above).
    @Watch("billingValidationResult", { immediate: true })
    applyBillingValidationClasses(result: BillingValidationResult) {
        if (result.fieldClasses.dispense) {
            this.validationClasses.dispense = result.fieldClasses.dispense;
        }
        this.validationClasses.writtenDate = result.fieldClasses.writtenDate;
    }

    updateAlerts() {
        this.entityAlertsComponent?.refresh();
    }

    startSurescriptsElectronicPriorAuthorization() {
        PriorAuthorizationService.createSurescripts(this.prescription)
            .then((resp) => {
                console.log(resp);
                this.$notification(
                    NotificationOptions.successSaveNotificationPreset(
                        "Electronic Prior Authorization",
                    ),
                );
            })
            .catch((err) => {
                this.$notification(
                    NotificationOptions.errorSaveNotificationPreset(
                        "Electronic Prior Authorization",
                        err,
                    ),
                );
            });
    }

    startSurescriptsElectronicPriorAuthorizationExpedited() {
        PriorAuthorizationService.createSurescriptsExpedited(this.prescription)
            .then((resp) => {
                console.log(resp);
                this.$notification(
                    NotificationOptions.successSaveNotificationPreset(
                        "Electronic Prior Authorization",
                    ),
                );
            })
            .catch((err) => {
                this.$notification(
                    NotificationOptions.errorSaveNotificationPreset(
                        "Electronic Prior Authorization",
                        err,
                    ),
                );
            });
    }

    onPharmacyFormClose() {
        this.showPharmacyForm = false;
        this.originPharmacy = new PharmacyRecord();
    }

    onPharmacyCreate(newPharma: PharmacyRecord) {
        this.showPharmacyForm = false;
        this.originPharmacy = new PharmacyRecord(newPharma);
    }

    contactTypeFormatter(value: Number): string {
        switch (value) {
            case 0:
                return "Text";
            case 1:
                return "Phone";
            case 2:
                return "Email";
            case 3:
                return "Auto Outbound";
            case 4:
                return "Auto Fax";
        }
        return "Unknown";
    }

    get prescriptionContactFields() {
        return [
            {
                key: "contactType",
                formatter: this.contactTypeFormatter,
            },
            {
                key: "contactDate",
                formatter: (value: Date) => moment(value).format("l LT"),
                sortable: true,
            },
            "contactMessage",
        ];
    }

    async prescriptionContacts(_ctx: BvTableCtxObject) {
        try {
            const contacts = await PrescriptionService.getContacts(this.prescription.rxID);
            return contacts.map((r) => new Prescription(r));
        } catch (error) {
            return [];
        }
    }

    async openDocument(pd: PatientDocument) {
        try {
            const blob = await ImageService.getImageBlob(pd.imageId);
            const objectUrl = URL.createObjectURL(blob);
            window.open(objectUrl, '_blank');
        } catch (err) {
            console.error("Error while getting image to show.", err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    @Watch('prescription')
    @Watch('patient.primaryLanguage')
    @Watch('prescription.directions')
    @Watch('drugNdc')
    @Watch('drug.productNameShort')
    async fetchTranslations() {
        this.directionsTranslations = {} as any;
        if (this.patient.primaryLanguage == 0) return;
        if (!(this.prescription.rxNumber > 0)) return;
        if (this.prescription.isExpired) return;
        if (!this.drugNdc) return;
        if (!this.drug.productNameShort) return;

        try {
            this.loadingDirections = true;
            this.directionsTranslations = await SigTranslationService.getTranslations(
                this.prescription.directions,
                this.drug.productNameShort,
                this.drugNdc,
            );
        } catch (err) {
            // ignore
        }
        this.loadingDirections = false;
    }

    @Watch('prescription')
    handleDispenseItemsChange() {
        const hasDispenseItems = !!this.prescription?.dispensingItems?.length;
        if (hasDispenseItems) {
            this.localDispenseItems = [...this.prescription.dispensingItems!];
            return;
        }

        const hasLegacyDispenseData = this.prescription.serialNumber || this.prescription.lotNumber || this.prescription.drugExpirationDate;
        if (hasLegacyDispenseData) {
            this.localDispenseItems = [
                {
                    lotNumber: this.prescription.lotNumber,
                    serialNumber: this.prescription.serialNumber,
                    expirationDate: this.prescription.drugExpirationDate,
                    quantity: null,
                },
            ];
        } else {
            this.localDispenseItems = [];
        }
    }

    async translateDirections() {
        if (!this.selectedTranslation) {
            this.$notification(NotificationOptions.error("Select directions to translate."));
            return;
        }
        this.loadingDirections = true;
        try {
            const translated = await SigTranslationService.translate(
                this.selectedTranslation,
                this.prescription.directions,
                this.patient.id,
            );
            const translation = this.directionsTranslations.paraphrases.find(p => p.id == this.selectedTranslation);
            const baseDirections = translation?.token ?? this.prescription.directions;
            const directionLines = [];
            if (baseDirections) {
                directionLines.push(`Directions Translated From: ${baseDirections}`);
            }
            if (this.prescription.memo) {
                directionLines.push(this.prescription.memo);
            }
            this.prescription.memo = directionLines.join("\n");
            this.prescription.directions = translated;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
        this.loadingDirections = false;
    }

    @Watch('doctor')
    async fetchDoctorProgram() {
        if (!this.doctor?.programId) {
            this.doctorProgram = new Program();
            return;
        }

        try {
            const response = await Axios.get<Program>("/api/Program/" + this.doctor.programId);
            this.doctorProgram = Object.assign(new Program, response.data);
        } catch {
            this.doctorProgram = new Program();
        }
    }

    autoselectStore(options: Array<Store>) {
        const activeStores = options.filter(o => o.active);
        return (activeStores.length !== 1) ? undefined : activeStores[0];
    }

    async createPAPApplication() {
        const confirmed = await this.$bvModal.msgBoxConfirm("Create a PAP application from this script?", {
            title: 'Create PAP Application',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'primary',
            okTitle: 'Create Application',
            cancelTitle: 'Cancel',
            footerClass: 'p-2',
            centered: true,
        });

        if (!confirmed) return;

        try {
            await this.$http.post(`/PatientAssistanceProgram/CreateFromScript/${this.prescription?.rxID}`);
            this.$notification(NotificationOptions.success("PAP created successfully."));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    protected dispenseErrorLabel = "";

    async fetchDispenseErrorLabel() {
        try {
            const resp = await this.$http.get("/Configuration/DispenseErrorLabel");
            this.dispenseErrorLabel = resp.data?.trim();
        } catch {
            this.dispenseErrorLabel = "Error";
            //ignore
        }
    }

    protected localDispenseItems: PrescriptionDispenseItem[] = [];

    get dispenseItems() {
        if (this.disableFields) return this.prescription.dispensingItems;
        return [...this.localDispenseItems, {}];
    }

    updateDispenseItem(key: string, index: number, value: any) {
        if (this.localDispenseItems.length === index) {
            this.localDispenseItems.push({});
        }

        const item = this.localDispenseItems[index];
        (item as any)[key] = value;

        this.localDispenseItems = this.localDispenseItems
            .filter(di => di.lotNumber || di.serialNumber || di.expirationDate || di.quantity);
    }

    onTransferCompleted(transferData: any) {
        if (transferData?.prescription) {
            this.prescription = Object.assign(new Prescription(), transferData.prescription);
            this.fetchPrescription(this.prescription);
        }

        this.updateAlerts();
    }

    async markAsNotAScript() {
        if (!this.queueItemID) {
            return;
        }

        try {
            // Create queue item with only the ID (no storeID/rxNumber/rfNumber = "Not A Script")
            const queueItem: QueueItem = {
                id: this.queueItemID
            };

            // Mark as complete (not a script)
            await IngestionQueueService.complete([queueItem]);

            this.$notification({
                message: 'Item marked as "Not A Script"',
                timeout: 3000,
                icon: "now-ui-icons ui-1_check",
                horizontalAlign: "center",
                verticalAlign: "top",
                type: "success"
            });

            if (this.popQueue()) {
                // Successfully popped to next queued item
                return;
            }

            const hasNextItem = await this.handleAutoModeCompletion();

            if (!hasNextItem && this.fromIngestion) {
                this.backToPreviousPage(true);
            }
        } catch (error) {
            console.error('Error marking as not a script:', error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    async handleAutoModeCompletion(): Promise<boolean> {
        return await this.handleAutoModeNextItem();
    }

    // The processing-screen "Move to Bucket" action is shown only for ingestion items, when the
    // tenant has opted in, and when the user holds the same permission the Rx Intake Queue enforces.
    get canMoveToBucket(): boolean {
        return !!this.queueItemID
            && this.showMoveToBucketOnProcessing
            && this.$permissions.some(p => p.permissionCodeName === "Processing/Rx Intake Queue/Move Item");
    }

    get moveToBucketOptions(): BucketOption[] {
        return buildMoveToBucketOptions(
            this.moveToBucketPrograms,
            this.moveToBucketStaticBuckets,
            this.moveToBucketDestinationFilter
        );
    }

    // Loads the destination programs/buckets when the modal opens. The two GETs settle
    // independently so one failure (e.g. a 403 on /IngestionQueue/Buckets) does not void the
    // other list, and any failure is surfaced to the user rather than swallowed.
    async loadMoveToBucketData(): Promise<void> {
        const [programsResult, bucketsResult] = await Promise.allSettled([
            Axios.get<Program[]>("/Program?active=true"),
            Axios.get<NamedRecord[]>("/IngestionQueue/Buckets"),
        ]);

        if (programsResult.status === "fulfilled") {
            this.moveToBucketPrograms = programsResult.value.data;
        }
        if (bucketsResult.status === "fulfilled") {
            this.moveToBucketStaticBuckets = bucketsResult.value.data;
        }

        const firstFailure = [programsResult, bucketsResult]
            .find((r): r is PromiseRejectedResult => r.status === "rejected");
        if (firstFailure) {
            console.error("Error loading move-to-bucket destinations:", firstFailure.reason);
            this.$notification(NotificationOptions.error(firstFailure.reason));
        }
    }

    openMoveToBucketModal(): void {
        this.selectedMoveToBucket = null;
        this.moveToBucketModalVisible = true;
        // Fetch fresh destinations each open — avoids a permissions race with the config load and
        // keeps the list current if programs/buckets changed mid-session.
        void this.loadMoveToBucketData();
    }

    // Extracted so specs can stub the POST without mocking axios (mirrors postConfigValues).
    routeQueueItem(queueItem: QueueItem): Promise<{ data: boolean }> {
        return Axios.post<boolean>("IngestionQueue/Route", queueItem);
    }

    // Reroutes the locked ingestion item to the chosen bucket, then advances to the next item using
    // the same batch/auto-mode/back sequence as completing an item (mirrors markAsNotAScript).
    async confirmMoveToBucket(): Promise<void> {
        if (!this.queueItemID || !this.selectedMoveToBucket) {
            return;
        }

        try {
            this.isMovingToBucket = true;

            const queueItem: QueueItem = {
                id: this.queueItemID,
                bucketType: this.selectedMoveToBucket.bucketType,
                bucket: this.selectedMoveToBucket.bucket,
            };

            const { data: moved } = await this.routeQueueItem(queueItem);

            // The Route proc OUTPUTs the routed Id only when a row matched; false means the item was
            // already completed/removed, so report failure instead of a phantom success + advance.
            if (!moved) {
                this.moveToBucketModalVisible = false;
                this.$notification(NotificationOptions.error(
                    "The item could not be moved. It may have already been completed or removed."));
                return;
            }

            this.moveToBucketModalVisible = false;
            this.$notification({
                message: "Item moved to bucket",
                timeout: 3000,
                icon: "now-ui-icons ui-1_check",
                horizontalAlign: "center",
                verticalAlign: "top",
                type: "success"
            });

            // Keep Auto Mode from handing the just-routed item straight back: the item stays
            // incomplete (it still needs processing in its new bucket), so remember it for this
            // session and have the next-item fetch skip it.
            if (this.$store.autoModeEnabled) {
                const routedIds: Array<number | string> = this.$store.autoModeRoutedItemIds ?? [];
                routedIds.push(this.queueItemID);
                this.$store.autoModeRoutedItemIds = routedIds;
            }

            if (this.popQueue()) {
                // Successfully popped to next queued item
                return;
            }

            const hasNextItem = await this.handleAutoModeCompletion();

            if (!hasNextItem && this.fromIngestion) {
                this.backToPreviousPage(true);
            }
        } catch (error: any) {
            // A 418 means another user holds the lock; show the lock dialog rather than a generic
            // error toast (which NotificationOptions.error itself warns against for 418).
            if (error?.response?.status === 418) {
                this.moveToBucketModalVisible = false;
                this.showLockedMessage(
                    EntityType.IngestionQueueItem,
                    this.queueItemID!.toString(),
                    `Queue Item ${this.queueItemID}`,
                    error.response.data);
            } else {
                console.error("Error moving item to bucket:", error);
                this.$notification(NotificationOptions.error(error));
            }
        } finally {
            this.isMovingToBucket = false;
        }
    }
}
</script>

<style lang="scss">
.office-contacts-popover .card:last-child {
    margin-bottom: 0;
}
</style>

<style scoped lang="scss">
.rx-actions {
    margin: 0 -4px 10px; // -4px cancels the buttons' 5px outer margin back to the theme's own 1px

    // Margin on the buttons, not a flex gap — the right group's buttons sit inside child components.
    ::v-deep .btn {
        margin-left: 5px;
        margin-right: 5px;
    }
}

/*noinspection CssUnusedSymbol*/
::v-deep .modal-dialog {
    right: 20px;
    position: absolute;
    min-width: 600px;
}

table.dispense-items ::v-deep(th) {
    font-weight: normal !important;
}

::v-deep .layout-toggle .btn {
    padding: 0.05rem 1rem;
    font-size: 0.75rem;
    line-height: 1.3;
}

::v-deep .layout-toggle .btn.active,
::v-deep .layout-toggle label.active.btn,
::v-deep .layout-toggle .btn-check:checked + .btn {
    background-color: #007bff !important;
    border-color: #007bff !important;
    color: #fff !important;
}
</style>
